import React, { useState, useEffect, useCallback } from "react";
import { format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, addDays, addMonths, subMonths, isToday } from "date-fns";
import { motion, AnimatePresence } from "framer-motion";
import { Button, Form, Modal } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import logger from "../../utils/logger";
import { writeRouteDragData, readRouteDragData, allowRouteDrop } from "../../utils/routeDragDrop";
import useDriverPointerDrag from "../../utils/useDriverPointerDrag";

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRoute, faCheckCircle, faAnglesLeft, faAnglesRight } from '@fortawesome/free-solid-svg-icons';

import { useAlert } from '../../providers/AlertProvider';
import { useAuthProvider } from '../../providers/AuthProvider';

import { ActivityIndicator } from '../custom/ActivityIndicator';
import SelectJobModal from '../custom/SelectJobModal';
import RouteContainer from '../custom/RouteContainer';
import JobComments from '../custom/JobComments';
import SingleValueInput from "./SingleValueInput";
import RequestPassword from "./RequestPassword";
import SelectDropdown from "./SelectDropdown";
import SubcontractorInput from "./SubcontractorInput";
import DeliveryDetailsModal from "./DeliveryDetailsModal";
import DemurrageModal from "./DemurrageModal";
import DemurrageDetailsModal from "./DemurrageDetailsModal";
import PodOffModal from "./PodOffModal";
import StartTimeModal from "./StartTimeModal";
import LocationMapModal from "../LocationMapModal";
import { ProgressTracker } from './ProgressTracker';

import { sortList, getDateRangeForQuery } from "../lib/functions";// Function to sort a list of objects

import "../css/RouteStyles.css";

// Api calls
import { fetch_all_routes, add_route, add_bulk_routes, delete_route, update_route, downloadRoute, fetch_notes, add_notes, update_notes, delete_notes, check_route_sync_status, unSubcontract, subcontractRoute, updateDemurrage, updateStartTime, generateJobNote, bulkCreateJobsWithProgress, get_next_drop_number, getRegularDeliveriesForDate } from "../../api/route";
import { fetchDeliveryDetailsByRouteRef, fetchDeliveryDetailsByDate } from "../../api/delivery_details";
import { fetch_all_customers, fetch_all_customer_deliveries, fetch_address_book } from "../../api/customers";
import { fetch_all_subcontractors } from "../../api/subcontractors";
import { sanitizeMoneyString } from "../lib/functions";
import { validatePassword } from "../../api/profiles";
import { HomePageHolidays } from '../../functions/home_page/home_page_holidays';
import { checkCompletions, getCompletionByUuid, getCompletionImageUrl, generateDeliveryNote } from '../../api/route_completions';
import { fetchInvoiceAccountCodes, generateInvoiceDrafts } from '../../api/invoices';

// Job Requests imports
import { fetch_routing_requests, cancel_routing_request, accept_routing_request, decline_routing_request, fetch_monthly_pending_requests } from "../../api/routing_requests";
import RequestJobModal from "./RequestJobModal";
import JobRequestsTable from "./JobRequestsTable";


const RouteCalendar = ({ theme, drivers, vehicles, notes }) => {
    // selectedVehicle is the vehicle id that is selected by the user to highlight the dates with service records
    // showServiceRecords is a boolean to show the service records instead of the calendar

    const { profile, isManager, isDev, permissions, features } = useAuthProvider();
    const canEdit = isDev || permissions.perm_routing_edit;
    const canSaveAddress = isDev || permissions.perm_customers_edit;
    const canUnlock = isDev || permissions.perm_routing_edit; // Super admins and devs can unlock a route
    const canInvoice = isDev || isManager || permissions.perm_invoice_edit;
    const { showAlert } = useAlert();
    const navigate = useNavigate();

    // Initialize holidays functions
    const holidaysFunctions = new HomePageHolidays(showAlert);

    const [isLoading, setIsLoading] = useState(false);
    const [customers, setCustomers] = useState([]);
    const [customerAccounts, setCustomerAccounts] = useState([]);
    const [addressBook, setAddressBook] = useState([]);

    const handleAddressSaved = (address) => {
        setAddressBook((current) => [...current.filter((item) => item.id !== address.id), address]
            .sort((first, second) => first.friendly_name.localeCompare(second.friendly_name)));
    };
    const [notesData, setNotesData] = useState([]);
    const [todaysNotes, setTodaysNotes] = useState({id: null, date: null, notes: null});
    const [showPrintPWModal, setShowPrintPWModal] = useState(false);
    const [selectedRoutesToPrint, setSelectedRoutesToPrint] = useState([]);
    const [showDeliveryNotesModal, setShowDeliveryNotesModal] = useState(false);
    const [selectedDeliveryNoteRoutes, setSelectedDeliveryNoteRoutes] = useState([]);
    const [isGeneratingDeliveryNotes, setIsGeneratingDeliveryNotes] = useState(false);
    const [currentDate, setCurrentDate] = useState(new Date());
    const [selectedDate, setSelectedDate] = useState(null);
    const [routeData, setRouteData] = useState([]);
    const [holidaysData, setHolidaysData] = useState([]);
    const [openSelectJob, setOpenSelectJob] = useState(false);
    const [routingRequests, setRoutingRequests] = useState([]);
    const [monthlyPendingRequests, setMonthlyPendingRequests] = useState([]);
    const [openRequestJob, setOpenRequestJob] = useState(false);
    const [savedSubcontractors, setSavedSubcontractors] = useState([]);

    const [openJobComments, setOpenJobComments] = useState(false);
    const [selectedRouteUuid, setSelectedRouteUuid] = useState(null);
    const [selectedJobComments, setSelectedJobComments] = useState(null);

    const [showCostings, setShowCostings] = useState(false);
    const [openCostings, setOpenCostings] = useState(false);

    const [openInvoiceTo, setOpenInvoiceTo] = useState(false);

    const [openDemurrage, setOpenDemurrage] = useState(false);
    const [selectedDemurrageData, setSelectedDemurrageData] = useState(null);

    const [openDemurrageDetails, setOpenDemurrageDetails] = useState(false);
    const [demurrageDetailsData, setDemurrageDetailsData] = useState(null);

    const [openAddTrailerReg, setOpenAddTrailerReg] = useState(false);

    const [openSelectFromDropdown, setOpenSelectFromDropdown] = useState(false);
    const [dropdownType, setDropdownType] = useState(null);

    const [openStartTimeModal, setOpenStartTimeModal] = useState(false);
    const [selectedStartTimeRoute, setSelectedStartTimeRoute] = useState(null);
    const [currentStartTime, setCurrentStartTime] = useState('');

    const [vehiclesServiceList, setVehiclesServiceList] = useState([]);
    const [vehiclesOverdueList, setVehiclesOverdueList] = useState([]);
    const [vorList, setVorList] = useState([]);

    // Route sync states for detecting changes
    const [lastKnownUpdate, setLastKnownUpdate] = useState(null);
    const [showSyncBanner, setShowSyncBanner] = useState(false);

    // Progress tracking states for bulk job creation
    const [isBulkProcessing, setIsBulkProcessing] = useState(false);
    const [bulkProgress, setBulkProgress] = useState({ current: 0, total: 0, percentage: 0 });
    const [bulkStatus, setBulkStatus] = useState({ message: '', type: 'info' });
    const [isPolling, setIsPolling] = useState(false);
    const [isGeneratingInvoiceDrafts, setIsGeneratingInvoiceDrafts] = useState(false);
    const [showInvoiceCodeModal, setShowInvoiceCodeModal] = useState(false);
    const [invoiceAccountCodes, setInvoiceAccountCodes] = useState([]);
    const [normalInvoiceAccountCodeId, setNormalInvoiceAccountCodeId] = useState('');
    const [networkInvoiceAccountCodeId, setNetworkInvoiceAccountCodeId] = useState('');
    const [invoiceRouteTypes, setInvoiceRouteTypes] = useState({ normal: false, network: false });

    // Request Password States =======================================================================================================
    const [openRequestPassword, setOpenRequestPassword] = useState(false);
    const [authorizedFunction, setAuthorizedFunction] = useState(null);

    // Route completion states =======================================================================================================
    const [completedRouteUuids, setCompletedRouteUuids] = useState({});
    const [showCompletionModal, setShowCompletionModal] = useState(false);
    const [selectedCompletionData, setSelectedCompletionData] = useState(null);
    const [selectedCompletionRoute, setSelectedCompletionRoute] = useState(null);
    
    // Image viewer states
    const [showImageModal, setShowImageModal] = useState(false);
    const [selectedImageUrl, setSelectedImageUrl] = useState(null);
    const [selectedImageTitle, setSelectedImageTitle] = useState('');
    const [isImageLoading, setIsImageLoading] = useState(false);
    
    // Maps states  
    const [showLocationModal, setShowLocationModal] = useState(false);
    const [selectedLocation, setSelectedLocation] = useState(null);

    // Delivery details modal states
    const [showDeliveryDetailsModal, setShowDeliveryDetailsModal] = useState(false);
    const [deliveryDetailsRoute, setDeliveryDetailsRoute] = useState(null);
    const [deliveryDetailsRecord, setDeliveryDetailsRecord] = useState(null);
    const [deliveryDetailsLoading, setDeliveryDetailsLoading] = useState(false);

    // Pod Off modal states
    const [showPodOffModal, setShowPodOffModal] = useState(false);
    const [podOffRoute, setPodOffRoute] = useState(null);

    // Set of route_uuids that have a late or delayed delivery record
    const [deliveryAlerts, setDeliveryAlerts] = useState(new Set());

    // Sidebar collapse state
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    const [useClassicRouteView, setUseClassicRouteView] = useState(false);

    // Get current day's routes for Print PW functionality
    const currentDayRoutes = selectedDate 
        ? routeData
            .filter((event) => format(new Date(event.date), "yyyy-MM-dd") === selectedDate)
            .sort((a, b) => {
                // Sort by job field, handling null/undefined values
                const jobA = a.job || '';
                const jobB = b.job || '';
                return jobA.localeCompare(jobB);
            })
        : [];

    const verifyPassword = async (authorizedFunction) => {
        setAuthorizedFunction(authorizedFunction); // Set the authorized function
        setOpenRequestPassword(true); // Open the modal
    };
    
    const handlePasswordSubmission = async (password) => {
        const email = profile.email;
    
        try {
            const { status } = await validatePassword(password, email);
            if (status === 200) {
                logger.log("Password is valid.");
                // Call the authorized function if the password is valid
                switch(authorizedFunction) {
                    case 'toggleRouteLock':
                        toggleRouteLock();
                        break;
                    case 'costings':
                        toggleShowCostings();
                        break;
                    default:
                        logger.log("No authorized function to call.");
                        break;
                };
            } else {
                logger.log("Invalid password.");
                showAlert({
                    message: 'Password verification failed.',
                    type: 'danger',
                    buttons: [{ type: 'danger', label: 'Close' }],
                });
            }
        } catch (error) {
            logger.error("Error verifying password:", error);
            showAlert({
                message: 'An error occurred during password verification.',
                type: 'danger',
                buttons: [{ type: 'danger', label: 'Close' }],
            });
        } finally {
            setAuthorizedFunction(null); // Reset the authorized function
        }
    };
    
    

    // DEBUG =======================================================================================================================
    

    // DEBUG =======================================================================================================================

    // Create a list of vehicles that have a service due this week
    useEffect(() => {
        const today = new Date();
        const startOfWeekDate = startOfWeek(today, { weekStartsOn: 1 }); // Monday start
        const endOfWeekDate = endOfWeek(today, { weekStartsOn: 1 }); // Sunday end
    
        const vehiclesThisWeek = vehicles
            .filter((vehicle) => {
                const serviceDate = new Date(vehicle.inspection_expiry);
                return serviceDate >= startOfWeekDate && serviceDate <= endOfWeekDate;
            })
            .map((vehicle) => vehicle.reg); // Extract only the registration numbers
    
        const vehiclesOverdue = vehicles
            .filter((vehicle) => {
                const serviceDate = new Date(vehicle.inspection_expiry);
                return serviceDate < startOfWeekDate;
            })
            .map((vehicle) => vehicle.reg); // Extract only the registration numbers

        const vorList = vehicles
            .filter((vehicle) => vehicle.vor === true)
            .map((vehicle) => vehicle.reg); // Extract only the registration numbers
        
        setVorList(vorList);
        setVehiclesServiceList(vehiclesThisWeek);
        setVehiclesOverdueList(vehiclesOverdue);
    }, [vehicles]);
    
    // Filter notes for the current date
    useEffect(() => {
        if (!notesData || !Array.isArray(notesData)) return;        

        // Find the note for the current date
        const foundNote = notesData?.find(
        (note) => note.date === format(selectedDate, "yyyy-MM-dd")
        );

        setTodaysNotes(
        foundNote
            ? { id: foundNote.id, date: foundNote.date, notes: foundNote.notes }
            : { id: null, date: null, notes: "" }
        );
    }, [selectedDate, notesData]);

    // Load job requests for the selected date
    const loadRoutingRequests = async (dateStr) => {
        if (!dateStr) return;
        const res = await fetch_routing_requests(dateStr);
        if (res.status === 200) {
            setRoutingRequests(res.data || []);
        }
    };

    // Fetch routing requests when selectedDate changes
    useEffect(() => {
        if (!selectedDate) {
            setRoutingRequests([]);
            return;
        }
        
        // Convert to string format YYYY-MM-DD if it is a Date object (just to be safe)
        const dateStr = typeof selectedDate === 'string' 
            ? selectedDate 
            : format(selectedDate, "yyyy-MM-dd");
            
        const fetchAllDayData = async () => {
            setIsLoading(true);
            await loadRoutingRequests(dateStr);
            setIsLoading(false);
        };

        fetchAllDayData();
    }, [selectedDate]);

    // Accept job request (gated)
    const handleAcceptRequest = async (requestId) => {
        setIsLoading(true);
        const res = await accept_routing_request(requestId);
        setIsLoading(false);
        if (res.status === 200) {
            showAlert({
                message: 'Request accepted and job added to routing successfully!',
                type: 'success',
                buttons: [{ type: 'success', label: 'Close' }],
            });
            // Update local route data to include the new route immediately
            if (res.data) {
                // customer_company is a virtual join field — copy it from the matching request
                const matchingRequest = routingRequests.find(r => r.id === requestId);
                setRouteData((prev) => [...prev, { ...res.data, customer_company: matchingRequest?.customer_company ?? null }]);
            }
            // Refresh request list
            loadRoutingRequests(selectedDate);
        } else {
            showAlert({
                message: res.error || 'Failed to accept request.',
                type: 'danger',
                buttons: [{ type: 'danger', label: 'Close' }],
            });
        }
    };

    // Decline job request (gated)
    const handleDeclineRequest = async (requestId, reason) => {
        setIsLoading(true);
        const res = await decline_routing_request(requestId, reason);
        setIsLoading(false);
        if (res.status === 200) {
            showAlert({
                message: 'Request declined successfully.',
                type: 'success',
                buttons: [{ type: 'success', label: 'Close' }],
            });
            // If there's an associated deleted route, remove it from the calendar view in real-time
            if (res.deleted_route?.route_uuid) {
                setRouteData((prev) => prev.filter((r) => r.route_uuid !== res.deleted_route.route_uuid));
            }
            // Refresh request list
            loadRoutingRequests(selectedDate);
        } else {
            showAlert({
                message: res.error || 'Failed to decline request.',
                type: 'danger',
                buttons: [{ type: 'danger', label: 'Close' }],
            });
        }
    };

    // Cancel job request
    const handleCancelRequest = async (requestId) => {
        setIsLoading(true);
        const res = await cancel_routing_request(requestId);
        setIsLoading(false);
        if (res.status === 200) {
            showAlert({
                message: 'Request cancelled successfully.',
                type: 'success',
                buttons: [{ type: 'success', label: 'Close' }],
            });
            // Refresh request list
            loadRoutingRequests(selectedDate);
        } else {
            showAlert({
                message: res.error || 'Failed to cancel request.',
                type: 'danger',
                buttons: [{ type: 'danger', label: 'Close' }],
            });
        }
    };

    // Check completion status for routes when a day is selected
    useEffect(() => {
        if (!selectedDate || !routeData.length) return;

        const checkDayCompletions = async () => {
            try {
                // Filter routes for the selected date
                const dayRoutes = routeData.filter((route) => 
                    format(new Date(route.date), "yyyy-MM-dd") === selectedDate
                );
                
                // Extract route UUIDs for the selected day
                const dayUuids = dayRoutes.map((r) => r.route_uuid).filter(Boolean);

                // Run completions check and delivery alerts fetch in parallel
                const [completionResult, alertsResult] = await Promise.all([
                    dayUuids.length > 0 ? checkCompletions(dayUuids) : Promise.resolve(null),
                    fetchDeliveryDetailsByDate(selectedDate),
                ]);

                if (completionResult) {
                    logger.log(`Checking completions for ${dayUuids.length} routes on ${selectedDate}`);
                    setCompletedRouteUuids(prev => ({
                        ...prev,
                        ...(completionResult?.completed ?? {})
                    }));
                    logger.log(`Completion status updated for day ${selectedDate}:`, completionResult?.completed);
                }

                // Build a Set of route_uuids that have a late or delayed delivery record
                if (alertsResult?.status === 200 && Array.isArray(alertsResult.data)) {
                    const alertUuids = new Set(
                        alertsResult.data
                            .filter(r => r.late || (r.delay_length !== null && r.delay_length > 60))
                            .map(r => r.route_uuid)
                    );
                    setDeliveryAlerts(alertUuids);
                }
            } catch (error) {
                logger.error('Error checking day completion status:', error);
            }
        };

        checkDayCompletions();
    }, [selectedDate, routeData]);

    // Helper function to update lastKnownUpdate when user makes changes
    const updateSyncTimestamp = () => {
        const now = new Date().toISOString();
        setLastKnownUpdate(now);
        return now;
    };

    // Check for route updates from other users
    const checkForUpdates = useCallback(async () => {
        if (isPolling || isLoading) return;
        
        try {
            const { data, status } = await check_route_sync_status();
            
            if (status === 200 && data?.last_updated) {
                if (lastKnownUpdate && data.last_updated > lastKnownUpdate) {
                    setShowSyncBanner(true);
                }
                
                // Update timestamp only if banner is not showing
                if (!showSyncBanner) {
                    setLastKnownUpdate(data.last_updated);
                }
            }
        } catch (error) {
            logger.error('Sync check failed:', error);
        }
    }, [lastKnownUpdate, showSyncBanner, isPolling, isLoading]);

    // Sync button handler
    const handleSync = async () => {
        setIsPolling(true);
        setShowSyncBanner(false);
        
        // Refresh route data
        await fetchData();
        
        // Update the last known timestamp
        try {
            const { data } = await check_route_sync_status();
            if (data?.last_updated) {
                setLastKnownUpdate(data.last_updated);
            }
        } catch (error) {
            logger.error('Error updating sync timestamp:', error);
        }
        
        setIsPolling(false);
    };

    // Fetching service data
    const fetchData = async () => {
        if (!currentDate)  return;

        setIsLoading(true);
        const range = getDateRangeForQuery(currentDate);
        logger.log("Fetching data for range: ", range.from, range.to);
        try {
            const [routeResponse, notesResponse, customerResponse, customerAccountsResponse, monthlyReqsResponse, addressBookResponse] = await Promise.all([
                fetch_all_routes(range),
                fetch_notes(range),
                fetch_all_customer_deliveries(),
                fetch_all_customers(),
                fetch_monthly_pending_requests(range.from, range.to),
                fetch_address_book(),
            ]);

            // Fetch saved sub-contractors (best-effort, non-blocking)
            if (isDev || permissions.perm_subcontractors_view) {
                fetch_all_subcontractors().then(r => {
                    if (r.status === 200) setSavedSubcontractors(r.data?.data || []);
                }).catch(() => {});
            }

            // Also fetch holidays for the same date range
            await holidaysFunctions.fetchHolidays(setHolidaysData, () => {}, range);

            const routeResponseData = routeResponse?.data ?? null;
            const fetchedRoutes = routeResponseData?.data ?? [];
            setRouteData(fetchedRoutes);

            // set notes
            const notesResponseData = notesResponse?.data ?? null;
            setNotesData(notesResponseData?.data ?? []);
            
            // set customers (now customer_deliveries with joined customer_company)
            const customerResponseData = customerResponse?.data ?? null;
            const activeCustomers = customerResponseData?.data?.filter((d) => d.status === 'active');

            // sort by customer_company then route_name
            const sortedCustomers = (activeCustomers ?? []).sort((a, b) => {
                const co = (a.customer_company || '').localeCompare(b.customer_company || '');
                return co !== 0 ? co : (a.route_name || '').localeCompare(b.route_name || '');
            });
            setCustomers(sortedCustomers ?? []);
            const accountsByCompany = new Map();
            (customerAccountsResponse?.status === 200 ? (customerAccountsResponse.data?.data || []) : [])
                .filter((customer) => customer.company)
                .forEach((customer) => {
                    const companyKey = customer.company.trim().toLowerCase();
                    const existing = accountsByCompany.get(companyKey);
                    if (!existing || Number(customer.id) < Number(existing.id)) {
                        accountsByCompany.set(companyKey, customer);
                    }
                });
            setCustomerAccounts(
                Array.from(accountsByCompany.values())
                    .sort((a, b) => (a.company || '').localeCompare(b.company || ''))
            );
            setAddressBook(addressBookResponse?.status === 200 ? (addressBookResponse.data?.data || []) : []);

            // Set monthly pending requests
            if (monthlyReqsResponse && monthlyReqsResponse.status === 200) {
                setMonthlyPendingRequests(monthlyReqsResponse.data || []);
            } else {
                setMonthlyPendingRequests([]);
            }

            // Update sync timestamp after successful data fetch
            try {
                const { data: syncData } = await check_route_sync_status();
                if (syncData?.last_updated) {
                    setLastKnownUpdate(syncData.last_updated);
                }
            } catch (syncError) {
                logger.error('Error updating sync timestamp:', syncError);
            }

        } catch (error) {
            logger.error("Error fetching data:", error);
        } finally {
            setTimeout(() => setIsLoading(false), 500);
        }
    };

    // Trigger the fetchData function on component mount
    useEffect(() => {
        fetchData();
    }, [currentDate]);

    // Set up polling for route updates
    useEffect(() => {
        const interval = setInterval(checkForUpdates, 120000); // Check every 2 minutes
        return () => clearInterval(interval);
    }, [checkForUpdates]);

    // show costings ==============================================================================================================

    const toggleShowCostings = () => {
        setShowCostings((prev) => !prev);
    }

    // Handle start time updates ==================================================================================================
    const handleToggleStartTime = (route_uuid, start_time) => {
        setSelectedStartTimeRoute(route_uuid);
        setCurrentStartTime(start_time || '08:00');
        setOpenStartTimeModal(true);
    };

    const saveStartTime = async (newStartTime) => {
        if (!selectedStartTimeRoute) return;

        // Generate timestamp and update sync status immediately
        const timestamp = updateSyncTimestamp();
        
        try {
            // Update start time in database using API function
            const { message, status, updated_at } = await updateStartTime(selectedStartTimeRoute, newStartTime);

            if (status === 200 || status === 201) {
                // Update the route in local state
                const updatedRoutes = routeData.map((route) => {
                    if (route.route_uuid === selectedStartTimeRoute) {
                        return { ...route, start_time: newStartTime, updated_at: updated_at || timestamp };
                    }
                    return route;
                });
                setRouteData(updatedRoutes);
                
                showAlert({
                    message: 'Start time updated successfully.',
                    type: 'success',
                    buttons: [{
                        type: 'success',
                        label: 'OK',
                    }],
                });
            } else {
                console.error('Failed to update start time:', message);
                showAlert({
                    message: message || 'Failed to update start time.',
                    type: 'danger',
                    buttons: [{
                        type: 'danger',
                        label: 'Close',
                    }],
                });
            }
        } catch (error) {
            console.error('Error updating start time:', error);
            showAlert({
                message: 'An error occurred while updating start time.',
                type: 'danger',
                buttons: [{
                    type: 'danger',
                    label: 'Close',
                }],
            });
        }

        // Reset modal state
        setSelectedStartTimeRoute(null);
        setCurrentStartTime('');
    }

    // Is the days route locked? ===================================================================================================
    const isLocked = (events) => {
        // check if the first event is locked (if so all events are locked for that day)
        return (events && events.length > 0) ? events[0].locked ?? true : false;
    };

    // Handle the selection of a job ==============================================================================================
    const handleSelectJob = () => {
        // show the model
        setOpenSelectJob((prev) => !prev);
    };

    // Function to check if route is locked =======================================================================================
    const isRouteLocked = (date) => {
        const route = routeData.find((route) => format(new Date(route.date), "yyyy-MM-dd") === date);
        return route?.locked ?? false;
    };

    // Print PW modal functions ==================================================================================================
    const handlePrintPW = async () => {
        const selectedRoutes = currentDayRoutes.filter(route => 
            selectedRoutesToPrint.includes(route.route_uuid)
        );
        
        if (selectedRoutes.length === 0) {
            showAlert({
                message: 'Please select at least one route to generate paperwork.',
                type: 'warning',
                buttons: [{
                    type: 'primary',
                    label: 'Close',
                }],
            });
            return;
        }
        
        try {
            console.log('Generating job note for routes:', selectedRoutes);
            
            // Call the API to generate job note
            const { message, status } = await generateJobNote(selectedRoutes);
            
            if (status === 200) {
                showAlert({
                    message: message,
                    type: 'success',
                    buttons: [{
                        type: 'primary',
                        label: 'Close',
                    }],
                });
            } else {
                showAlert({
                    message: message || 'Failed to generate job note',
                    type: 'danger',
                    buttons: [{
                        type: 'danger',
                        label: 'Close',
                    }],
                });
            }
        } catch (error) {
            logger.error('Error generating job note:', error);
            showAlert({
                message: 'An error occurred while generating job note.',
                type: 'danger',
                buttons: [{
                    type: 'danger',
                    label: 'Close',
                }],
            });
        }
        
        setShowPrintPWModal(false);
        setSelectedRoutesToPrint([]);
    };

    const handleRouteSelection = (routeUuid, isSelected) => {
        if (isSelected) {
            setSelectedRoutesToPrint(prev => [...prev, routeUuid]);
        } else {
            setSelectedRoutesToPrint(prev => prev.filter(uuid => uuid !== routeUuid));
        }
    };

    const handleSelectAllRoutes = (isSelected) => {
        if (isSelected) {
            const allRouteUuids = currentDayRoutes.map(route => route.route_uuid);
            setSelectedRoutesToPrint(allRouteUuids);
        } else {
            setSelectedRoutesToPrint([]);
        }
    };

    // Delivery Notes handlers =====================================================================
    const completedRoutesForDay = currentDayRoutes.filter(r => completedRouteUuids[r.route_uuid] === true);

    const handleDeliveryNoteSelection = (routeUuid, isSelected) => {
        if (isSelected) {
            setSelectedDeliveryNoteRoutes(prev => [...prev, routeUuid]);
        } else {
            setSelectedDeliveryNoteRoutes(prev => prev.filter(uuid => uuid !== routeUuid));
        }
    };

    const handleSelectAllDeliveryNotes = (isSelected) => {
        if (isSelected) {
            setSelectedDeliveryNoteRoutes(completedRoutesForDay.map(r => r.route_uuid));
        } else {
            setSelectedDeliveryNoteRoutes([]);
        }
    };

    const handleGenerateDeliveryNotes = async () => {
        const selectedRoutes = completedRoutesForDay.filter(r =>
            selectedDeliveryNoteRoutes.includes(r.route_uuid)
        );
        if (selectedRoutes.length === 0) return;

        setIsGeneratingDeliveryNotes(true);
        let successCount = 0;
        let errorCount = 0;

        for (let i = 0; i < selectedRoutes.length; i++) {
            const route = selectedRoutes[i];
            try {
                const completionResult = await getCompletionByUuid(route.route_uuid);
                if (!completionResult?.success || !completionResult?.completion) {
                    errorCount++;
                    continue;
                }
                const comp = completionResult.completion;
                logger.log('[DeliveryNote] completion data:', {
                    signed_by: comp.signed_by,
                    has_signature: !!comp.customer_signature_data,
                    sig_type: typeof comp.customer_signature_data,
                    sig_preview: comp.customer_signature_data ? String(comp.customer_signature_data).slice(0, 80) : 'NONE',
                    delivery_images: (comp.delivery_images || []).length,
                });
                const { status } = await generateDeliveryNote(route, comp);
                if (status === 200) {
                    successCount++;
                } else {
                    errorCount++;
                }
                if (i < selectedRoutes.length - 1) {
                    await new Promise(resolve => setTimeout(resolve, 500));
                }
            } catch (err) {
                logger.error('Error generating delivery note:', err);
                errorCount++;
            }
        }

        setIsGeneratingDeliveryNotes(false);
        setShowDeliveryNotesModal(false);
        setSelectedDeliveryNoteRoutes([]);

        showAlert({
            message: errorCount === 0
                ? `${successCount} delivery note${successCount !== 1 ? 's' : ''} downloaded successfully.`
                : `${successCount} downloaded, ${errorCount} failed.`,
            type: errorCount === 0 ? 'success' : 'warning',
            buttons: [{ type: 'primary', label: 'Close' }],
        });
    };

    // Function to check if a user is on holiday for a specific date ===============================================================
    const isUserOnHoliday = (userId, date) => {
        return holidaysData.some(holiday => 
            holiday.user_id === userId && holiday.date === date
        );
    };

    // Toggle the route lock =========================================================================================
    // @Authorized Function
    const toggleRouteLock = async () => {
        try {
            // Update the route in the database
            const { status } = await update_route(
                { 'locked': !isRouteLocked(selectedDate) },
                selectedDate
            );

            if (status !== 200) {
                // If the update fails, show an alert
                showAlert({
                    message: 'Failed to update route.',
                    type: 'danger',
                    buttons: [{
                        type: 'danger',
                        label: 'Close',
                    }],
                });
            } else {
                // Update the route data in the local state
                setRouteData((prev) => {
                    const updatedRoutes = prev.map((route) => {
                        if (format(new Date(route.date), "yyyy-MM-dd") === selectedDate) {
                            return { ...route, locked: !route.locked };
                        }
                        return route;
                    });
                    return updatedRoutes;
                });
            }
        } catch (error) {
            // Handle any errors from the database operation
            logger.error('Error updating route:', error);
            showAlert({
                message: 'An error occurred while updating the route.',
                type: 'danger',
                buttons: [{
                    type: 'danger',
                    label: 'Close',
                }],
            });
        }
    };

    const handleGenerateInvoiceDrafts = async () => {
        const response = await fetchInvoiceAccountCodes();
        if (response.status !== 200) {
            showAlert({
                message: response.data?.error || 'Unable to load invoice account codes.',
                type: 'danger',
                buttons: [{ type: 'danger', label: 'Close' }],
            });
            return;
        }
        setInvoiceAccountCodes(response.data.account_codes || []);
        const selectedDayRoutes = routeData.filter(
            route => format(new Date(route.date), "yyyy-MM-dd") === selectedDate
        );
        setInvoiceRouteTypes({
            normal: selectedDayRoutes.some(route => !route.is_network_freight),
            network: selectedDayRoutes.some(route => route.is_network_freight),
        });
        setNormalInvoiceAccountCodeId('');
        setNetworkInvoiceAccountCodeId('');
        setShowInvoiceCodeModal(true);
    };

    const generateSelectedInvoiceDrafts = async () => {
        if (
            (invoiceRouteTypes.normal && !normalInvoiceAccountCodeId)
            || (invoiceRouteTypes.network && !networkInvoiceAccountCodeId)
        ) return;
        setShowInvoiceCodeModal(false);
        setIsGeneratingInvoiceDrafts(true);
        const response = await generateInvoiceDrafts(
            selectedDate,
            normalInvoiceAccountCodeId ? Number(normalInvoiceAccountCodeId) : null,
            networkInvoiceAccountCodeId ? Number(networkInvoiceAccountCodeId) : null,
        );
        setIsGeneratingInvoiceDrafts(false);

        if (response.status !== 200 && response.status !== 201) {
            showAlert({
                message: response.data?.error || 'Unable to generate invoice drafts.',
                type: 'danger',
                buttons: [{ type: 'danger', label: 'Close' }],
            });
            return;
        }

        const draftCount = response.data?.draft_count || 0;
        const routeCount = response.data?.route_count || 0;
        const exceptionCount = response.data?.exceptions?.length || 0;
        const exceptionText = exceptionCount
            ? ` ${exceptionCount} route${exceptionCount === 1 ? '' : 's'} require attention or were already allocated.`
            : '';
        showAlert({
            message: draftCount
                ? `${draftCount} draft invoice${draftCount === 1 ? '' : 's'} created for ${routeCount} route${routeCount === 1 ? '' : 's'}.${exceptionText}`
                : `No new invoice drafts were created.${exceptionText}`,
            type: draftCount ? 'success' : 'warning',
            buttons: [
                { type: 'secondary', label: 'Close' },
                { type: 'primary', label: 'Open invoice register', onClick: () => navigate('/invoices') },
            ],
        });
    };
    


    // Add a new job to the calendar ==================================================================================================
    const addJob = async (jobData) => {
        // Generate timestamp and update sync status immediately
        const timestamp = updateSyncTimestamp();
        
        // Add timestamp to job data
        const jobDataWithTimestamp = {
            ...jobData,
            updated_at: timestamp
        };
        
        // add route to database
        const { status, data } = await add_route(jobDataWithTimestamp)
        if (status !== 200) {
            showAlert({
                message: 'Failed to add route to database.',
                type: 'danger',
                buttons: [{
                    type: 'danger',
                    label: 'Close',
                }],
            });
        } else {
            // Add the job data returned from database (includes route_ref) to the routeData
            const createdJobData = data && data.length > 0 ? data[0] : jobDataWithTimestamp;
            // Preserve customer_company from submitted data — not stored in DB, comes from join on fetch
            setRouteData((prev) => [...prev, { ...createdJobData, customer_company: jobData.customer_company }]);
        }
    };

    // Add multiple jobs to the calendar (bulk mode) with progress tracking ==============================================================================
    const syncJobs = () => {
        setOpenSelectJob(false);
        showAlert({
            message: 'Are you sure you want to sync daily jobs?',
            type: 'warning',
            buttons: [
                {
                    type: 'primary',
                    label: 'Yes',
                    onClick: async () => {
                        handleSyncDailyJobs();
                    }
                },
                {
                    type: 'primary',
                    label: 'Close',
                }
            ],
        });
        return;
    }

    const handleSyncDailyJobs = async () => {
        if (!selectedDate) return;
        try {
            const syncData = await getRegularDeliveriesForDate(selectedDate);
            if (syncData.status === 200 && syncData.data && syncData.data.length > 0) {
                addBulkJobs(syncData.data);
            }
        } catch (error) {
            showAlert({
                message: 'Failed to sync daily jobs.',
                type: 'danger',
                buttons: [{
                    type: 'danger',
                    label: 'Close',
                }],
            });
        }
    }

    const addBulkJobs = async (jobsData) => {
        // Generate timestamp for all jobs
        console.log('Adding bulk jobs:', jobsData);
        const timestamp = updateSyncTimestamp();
        
        // Add timestamp to all jobs
        const jobsWithTimestamp = jobsData.map(job => ({
            ...job,
            updated_at: timestamp
        }));
        
        try {
            setIsBulkProcessing(true);
            setBulkProgress({ current: 0, total: jobsData.length, percentage: 0 });
            setBulkStatus({ message: 'Starting bulk job creation...', type: 'info' });

            // Use progress tracking for bulk job creation
            await bulkCreateJobsWithProgress(jobsWithTimestamp, {
                onStart: () => {
                    setBulkStatus({ message: 'Bulk job creation started...', type: 'info' });
                },
                onProgress: (progress) => {
                    setBulkProgress({
                        current: progress.processed || 0,
                        total: progress.total || jobsData.length,
                        percentage: progress.total ? Math.round((progress.processed / progress.total) * 100) : 0
                    });
                    setBulkStatus({
                        message: progress.message || `Creating jobs: ${progress.processed}/${progress.total}`,
                        type: 'info'
                    });
                },
                onComplete: (finalProgress) => {
                    setBulkProgress({
                        current: finalProgress.total,
                        total: finalProgress.total,
                        percentage: 100
                    });
                    setBulkStatus({
                        message: `✅ Successfully created ${finalProgress.success_count} jobs!${finalProgress.error_count > 0 ? ` (${finalProgress.error_count} errors)` : ''}`,
                        type: finalProgress.error_count > 0 ? 'warning' : 'success'
                    });
                    
                    // Update the UI by adding the jobs that were successfully created
                    if (finalProgress.success_count > 0) {
                        // Build a lookup so we can restore customer_company (virtual join field, not stored in DB)
                        const submittedByUuid = Object.fromEntries(
                            jobsWithTimestamp.map(j => [j.route_uuid, j])
                        );
                        const jobsToAdd = finalProgress.created_jobs && finalProgress.created_jobs.length > 0
                            ? finalProgress.created_jobs.map(j => ({
                                ...j,
                                customer_company: submittedByUuid[j.route_uuid]?.customer_company ?? null,
                              }))
                            : jobsWithTimestamp.slice(0, finalProgress.success_count);
                        setRouteData((prev) => [...prev, ...jobsToAdd]);
                    }
                    
                    // Hide progress tracker after 3 seconds
                    setTimeout(() => {
                        setIsBulkProcessing(false);
                        setBulkProgress({ current: 0, total: 0, percentage: 0 });
                        setBulkStatus({ message: '', type: 'info' });
                    }, 3000);
                },
                onError: (error) => {
                    setBulkStatus({
                        message: `❌ Error: ${error}`,
                        type: 'danger'
                    });
                    
                    // Hide progress tracker after 5 seconds on error
                    setTimeout(() => {
                        setIsBulkProcessing(false);
                        setBulkProgress({ current: 0, total: 0, percentage: 0 });
                        setBulkStatus({ message: '', type: 'info' });
                    }, 5000);
                },
                onFinally: () => {
                    // Don't immediately hide - let completion/error handlers control timing
                }
            });

        } catch (error) {
            setBulkStatus({
                message: `❌ Error: ${error.message}`,
                type: 'danger'
            });
            
            setTimeout(() => {
                setIsBulkProcessing(false);
                setBulkProgress({ current: 0, total: 0, percentage: 0 });
                setBulkStatus({ message: '', type: 'info' });
            }, 5000);
        }
    };

    // Delete a route from the calendar ==================================================================================================
    const handleDeleteRoute = async (uuid) => {
        // delete route from database
        const { status } = await delete_route(uuid)
        if (status !== 200) {
            showAlert({
                message: 'Failed remove route from database.',
                type: 'danger',
                buttons: [{
                    type: 'danger',
                    label: 'Close',
                }],
            });
        } else {
            logger.log("deleted route with uuid: ", uuid, "updating state...");
            // Delete the route from the routeData
            const updatedRoutes = routeData.filter((route) => route.route_uuid !== uuid);
            setRouteData(updatedRoutes);
        }
    };

    // Move a route to a new date ========================================================================================================
    const handleMoveRoute = async (route_uuid, newDate) => {
        const { status } = await update_route({ date: newDate }, route_uuid);
        if (status !== 200) {
            showAlert({
                message: 'Failed to move route.',
                type: 'danger',
                buttons: [{ type: 'danger', label: 'Close' }],
            });
        } else {
            // Remove route from current view (it now belongs to a different date)
            setRouteData((prev) => prev.filter((route) => route.route_uuid !== route_uuid));
        }
    };

    // remove driver from route =========================================================================================================
    const handleRemoveDriver = async (route_uuid) => {
        // Generate timestamp and update sync status immediately
        const timestamp = updateSyncTimestamp();
        
        // update the database — clear both driver and drop_number
        const { status } = await update_route({
            'profile_id': null,
            'drop_number': null,
            'updated_at': timestamp
        }, route_uuid);

        if (status !== 200) {
            showAlert({
                message: 'Failed to remove driver.',
                type: 'danger',
                buttons: [{
                    type: 'danger',
                    label: 'Close',
                }],
            });
        } else {
            // Delete the driver from the route
            const updatedRoutes = routeData.map((route) => {
                if (route.route_uuid === route_uuid) {
                    return { ...route, driver: null, driver_id: null, profile_id: null, drop_number: null };
                    }
                    return route;
                    });
            setRouteData(updatedRoutes);
        }
    };

    // remove vehicle from route =========================================================================================================
    const handleRemoveVehicle = async (route_uuid, type) => {
        // Generate timestamp and update sync status immediately
        const timestamp = updateSyncTimestamp();
        
        // update the database
        const updateData = type === 'trailer' ? 
            {'trailer_id': null, 'trailer_reg': null, 'trailer_type': null, 'updated_at': timestamp}
        :
            {'truck_id': null, 'truck_reg': null, 'truck_type': null, 'updated_at': timestamp};
            
        const { status } = await update_route(updateData, route_uuid);

        if (status !== 200) {
            showAlert({
                message: 'Failed to remove vehicle.',
                type: 'danger',
                buttons: [{
                    type: 'danger',
                    label: 'Close',
                }],
            });
        } else {
            // Delete the vehicle from the route
            const updatedRoutes = routeData.map((route) => {
                if (route.route_uuid === route_uuid) {
                    return { 
                        ...route, 
                        trailer_reg: type === 'trailer' ? null : route.trailer_reg,
                        trailer_id: type === 'trailer' ? null : route.trailer_id,
                        trailer_type: type === 'trailer' ? null : route.trailer_type,
                        external_trailer: type === 'trailer' ? null : route.external_trailer,
                        truck_reg: type === 'truck' ? null : route.truck_reg,
                        truck_id: type === 'truck' ? null : route.truck_id,
                        truck_type: type === 'truck' ? null : route.truck_type,
                    };
                }
                return route;
            });
            
            setRouteData(updatedRoutes);
        }
    };


    // Job comments ========================================================================================================

    const handleToggleJobComment = (route_uuid, job_comments) => {
        setSelectedRouteUuid(route_uuid);
        setSelectedJobComments(job_comments);
        setOpenJobComments((prev) => !prev);
    };

    const addNewComments = async (newComment, route_uuid) => {
        // Generate timestamp and update sync status immediately
        const timestamp = updateSyncTimestamp();
        
        // update the database
        const { status } = await update_route({
            'job_comments': newComment,
            'updated_at': timestamp
        }, route_uuid);

        if (status !== 200) {
            showAlert({
                message: 'Failed to update comments.',
                type: 'danger',
                buttons: [{
                    type: 'danger',
                    label: 'Close',
                }],
            });
        } else {
            // Add the new comment to the route
            const updatedRoutes = routeData.map((route) => {
                if (route.route_uuid === route_uuid) {
                    return { ...route, job_comments: newComment };
                }
                return route;
            });
            setRouteData(updatedRoutes); // Update the routes state with the new job comment
        }
        // reset the selected route id
        // reset the selected job comments
        setSelectedJobComments(null);
        setSelectedRouteUuid(null);
        setOpenJobComments(false);
    };

    // handle costings ========================================================================================================

    const handleToggleJobCostings = (route_uuid) => {
        setSelectedRouteUuid(route_uuid);
        setOpenCostings((prev) => !prev);
    };

    const addNewCostings = async (newCostings, route_uuid) => {
        // sanitize data
        const sanitizedCost = sanitizeMoneyString(newCostings);
        
        // Generate timestamp and update sync status immediately
        const timestamp = updateSyncTimestamp();

        // update the database
        const { status } = await update_route({
            'costings': sanitizedCost,
            'updated_at': timestamp
        }, route_uuid);

        if (status !== 200) {
            showAlert({
                message: 'Failed to update costings.',
                type: 'danger',
                buttons: [{
                    type: 'danger',
                    label: 'Close',
                }],
            });
        } else {
            // Add the new costings to the route
            const updatedRoutes = routeData.map((route) => {
                if (route.route_uuid === route_uuid) {
                    return { ...route, costings: sanitizedCost };
                    }
                    return route;
                });
            setRouteData(updatedRoutes); // Update the routes state with the new job comment
        }

        // reset the selected route id
        setSelectedRouteUuid(null);
        setOpenCostings(false);
    };

    // handle costings ========================================================================================================

    const handleToggleInvoiceTo = (route_uuid) => {
        setSelectedRouteUuid(route_uuid);
        setOpenInvoiceTo((prev) => !prev);
    };

    const addNewInvoiceTo = async (newInvoice, route_uuid) => {
        // Generate timestamp and update sync status immediately
        const timestamp = updateSyncTimestamp();
        
        // Add the new costings to the route
        const updatedRoutes = routeData.map((route) => {
            if (route.route_uuid === route_uuid) {
                return { ...route, invoice_id: newInvoice };
                }
                return route;
            });
        setRouteData(updatedRoutes); // Update the routes state with the new job comment
        setSelectedRouteUuid(null);
        setOpenInvoiceTo(false);

        // update the database
        const { status } = await update_route({
            'invoice_to': newInvoice,
            'updated_at': timestamp
        }, route_uuid);

        if (status !== 200) {
            showAlert({
                message: 'Failed to update invoice company.',
                type: 'danger',
                buttons: [{
                    type: 'danger',
                    label: 'Close',
                }],
            });
        }
    };

    // Delivery Details =====================================================================================================

    const handleOpenDeliveryDetails = async (route_uuid) => {
        const route = routeData.find(r => r.route_uuid === route_uuid);
        if (!route) return;

        const collectionAddress = addressBook.find(address => address.id === route.collection_address_id);
        const deliveryAddress = addressBook.find(address => address.id === route.delivery_address_id);
        setDeliveryDetailsRoute({
            ...route,
            collection_friendly_name: collectionAddress?.friendly_name,
            delivery_friendly_name: deliveryAddress?.friendly_name,
        });
        setDeliveryDetailsRecord(null);
        setDeliveryDetailsLoading(true);
        setShowDeliveryDetailsModal(true);

        if (route.route_ref) {
            const { status, data } = await fetchDeliveryDetailsByRouteRef(route.route_ref);
            if (status === 200 && data) {
                setDeliveryDetailsRecord(data);
            }
            // 200 with null data = no record yet — stays null so modal shows create form
        }
        setDeliveryDetailsLoading(false);
    };

    const handleCloseDeliveryDetails = (savedRecord) => {
        if (savedRecord) {
            setDeliveryDetailsRecord(savedRecord);
        }
        setShowDeliveryDetailsModal(false);
        setDeliveryDetailsRoute(null);
        setDeliveryDetailsRecord(null);
    };

    // Demurrage ========================================================================================================

    const handleToggleDemurrage = (route_uuid) => {

        // Find the actual route in routeData to check current demurrage values
        const currentRoute = routeData.find(route => route.route_uuid === route_uuid);
        
        if (currentRoute && currentRoute.demurrage && currentRoute.demurrage_cost) {
            // Demurrage exists - show details modal with option to remove
            setDemurrageDetailsData(currentRoute);
            setOpenDemurrageDetails(true);
        } else {
            // No demurrage exists - open modal to add new demurrage
            setSelectedRouteUuid(route_uuid);
            setSelectedDemurrageData({
                route_uuid,
                demurrage: null,
                demurrage_reason: null,
                demurrage_cost: null
            });
            setOpenDemurrage(true);
        }
    };

    const handleCloseDemurrageDetails = () => {
        setOpenDemurrageDetails(false);
        setDemurrageDetailsData(null);
    };

    // Route completion handlers ======================================================================================================
    const handleViewCompletion = async (route_uuid, routeInfo = null) => {
        try {
            const result = await getCompletionByUuid(route_uuid);
            if (result?.success && result?.completion) {
                setSelectedCompletionData(result.completion);
                setSelectedCompletionRoute(routeInfo);
                setShowCompletionModal(true);
            } else {
                // Check if it's an access denied error
                const errorMessage = result?.error || 'Could not load completion details.';
                const isAccessDenied = errorMessage.toLowerCase().includes('access denied') || 
                                     errorMessage.toLowerCase().includes('forbidden');
                
                showAlert({
                    message: isAccessDenied 
                        ? 'Access denied. You can only view completion details for routes you personally completed.'
                        : errorMessage,
                    type: 'warning',
                    buttons: [{ type: 'primary', label: 'Close' }],
                });
            }
        } catch (error) {
            logger.error('Error fetching completion data:', error);
            showAlert({
                message: 'An error occurred while loading completion details.',
                type: 'danger', 
                buttons: [{ type: 'danger', label: 'Close' }],
            });
        }
    };

    // Pod Off handlers ======================================================================================================
    const handlePodOff = (route_uuid) => {
        const route = routeData.find(r => r.route_uuid === route_uuid);
        if (!route) return;
        setPodOffRoute(route);
        setShowPodOffModal(true);
    };

    const handlePodOffSuccess = (route_uuid) => {
        // Mark route as completed in local state
        setCompletedRouteUuids(prev => ({ ...prev, [route_uuid]: true }));
        showAlert({
            message: 'Job has been manually podded off successfully.',
            type: 'success',
            buttons: [{ type: 'success', label: 'Close' }],
        });
    };

    const handleRemoveDemurrage = async () => {
        if (!demurrageDetailsData) return;
        
        // Call saveDemurrage with null values to remove demurrage
        await saveDemurrage({
            route_uuid: demurrageDetailsData.route_uuid,
            demurrage: null,
            demurrage_reason: null,
            demurrage_cost: null
        });
        
        // Close the details modal
        handleCloseDemurrageDetails();
    };

    const saveDemurrage = async (demurrageData) => {
        // Generate timestamp and update sync status immediately
        const timestamp = updateSyncTimestamp();
        
        const { status, updated_at } = await updateDemurrage(demurrageData.route_uuid, {
            demurrage: demurrageData.demurrage,
            demurrage_reason: demurrageData.demurrage_reason,
            demurrage_cost: demurrageData.demurrage_cost
        }, timestamp);

        if (status !== 200) {
            showAlert({
                message: 'Failed to update demurrage.',
                type: 'danger',
                buttons: [{
                    type: 'danger',
                    label: 'Close',
                }],
            });
        } else {
            // Update the route in local state
            const updatedRoutes = routeData.map((route) => {
                if (route.route_uuid === demurrageData.route_uuid) {
                    return { 
                        ...route, 
                        demurrage: demurrageData.demurrage,
                        demurrage_reason: demurrageData.demurrage_reason,
                        demurrage_cost: demurrageData.demurrage_cost,
                        updated_at: updated_at
                    };
                }
                return route;
            });
            setRouteData(updatedRoutes);
            
            showAlert({
                message: demurrageData.demurrage ? 'Demurrage updated successfully.' : 'Demurrage removed successfully.',
                type: 'success',
                buttons: [{
                    type: 'success',
                    label: 'OK',
                }],
            });
        }
        
        // Close modal and reset states
        setOpenDemurrage(false);
        setSelectedRouteUuid(null);
        setSelectedDemurrageData(null);
    };

    // Check if driver is assigned to a route =====================================================================================
    const isDriverAssigned = (driver_id) => {
        // filter routes for selected day
        const todaysRoutes = routeData.filter((route) => format(new Date(route.date), "yyyy-MM-dd") === selectedDate);
        return todaysRoutes.some((route) => route.profile_id === driver_id);
    };

    // Toggle the confirmed status of a route =====================================================================================
    const toggleRouteConfirmed = async (route_uuid, currentState) => {
        // Generate timestamp and update sync status immediately
        const timestamp = updateSyncTimestamp();
        
        // Update the route in the database
        const { status } = await update_route({
            'confirmed': !currentState,
            'updated_at': timestamp
        }, route_uuid);
        
        if (status !== 200) {
            // If the update fails, show an alert
            showAlert({
                message: 'Failed to update route confirmed status.',
                type: 'danger',
                buttons: [{
                    type: 'danger',
                    label: 'Close',
                }],
            });
        } else {
            // Update the route state
            const updatedRoutes = routeData.map((route) => {
                if (route.route_uuid === route_uuid) {
                    return { ...route, confirmed: !currentState };
                    }
                    return route;
                });
            setRouteData(updatedRoutes);
        };
    };

    const confirmAllForDriver = async (driverRoutes) => {
        // Confirm every route for this driver on the selected date
        const timestamp = updateSyncTimestamp();
        const results = await Promise.all(
            driverRoutes.map((r) =>
                update_route({ confirmed: true, updated_at: timestamp }, r.route_uuid)
            )
        );
        const allOk = results.every(({ status }) => status === 200);
        if (!allOk) {
            showAlert({
                message: 'Some routes failed to confirm. Please try again.',
                type: 'danger',
                buttons: [{ type: 'danger', label: 'Close' }],
            });
        }
        // Optimistic: update state regardless (failures shown above)
        const uuids = new Set(driverRoutes.map((r) => r.route_uuid));
        setRouteData((prev) =>
            prev.map((r) => uuids.has(r.route_uuid) ? { ...r, confirmed: true } : r)
        );
    };

    const unconfirmAllForDriver = async (driverRoutes) => {
        const timestamp = updateSyncTimestamp();
        const results = await Promise.all(
            driverRoutes.map((r) =>
                update_route({ confirmed: false, updated_at: timestamp }, r.route_uuid)
            )
        );
        const allOk = results.every(({ status }) => status === 200);
        if (!allOk) {
            showAlert({
                message: 'Some routes failed to unconfirm. Please try again.',
                type: 'danger',
                buttons: [{ type: 'danger', label: 'Close' }],
            });
        }
        const uuids = new Set(driverRoutes.map((r) => r.route_uuid));
        setRouteData((prev) =>
            prev.map((r) => uuids.has(r.route_uuid) ? { ...r, confirmed: false } : r)
        );
    };
    
    // Image viewer handlers
    const handleImageClick = async (imagePath, imageTitle) => {
        setSelectedImageTitle(imageTitle);
        setIsImageLoading(true);
        setShowImageModal(true);
        const result = await getCompletionImageUrl(imagePath);
        setIsImageLoading(false);
        if (result.success && result.image_url) {
            setSelectedImageUrl(result.image_url);
        } else {
            setShowImageModal(false);
            showAlert({ message: 'Failed to load image.', type: 'danger', buttons: [{ type: 'danger', label: 'Close' }] });
        }
    };

    const handleCloseImageModal = () => {
        setShowImageModal(false);
        setSelectedImageUrl(null);
        setSelectedImageTitle('');
        setIsImageLoading(false);
    };

    const handleDownloadAll = async (imagePaths, label) => {
        if (!imagePaths || imagePaths.length === 0) return;
        for (let i = 0; i < imagePaths.length; i++) {
            const result = await getCompletionImageUrl(imagePaths[i]);
            if (result.success && result.image_url) {
                try {
                    const response = await fetch(result.image_url);
                    const blob = await response.blob();
                    const ext = blob.type.split('/')[1] || 'jpg';
                    const objectUrl = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = objectUrl;
                    link.download = `${label}_${i + 1}.${ext}`;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    window.URL.revokeObjectURL(objectUrl);
                    await new Promise(r => setTimeout(r, 300));
                } catch (err) {
                    logger.error('Download failed for image', imagePaths[i], err);
                }
            }
        }
    };

    // Location handlers
    const handleLocationClick = (lat, lng, address) => {
        logger.log('Location clicked:', lat, lng, address);
        setSelectedLocation({
            lat: parseFloat(lat),
            lng: parseFloat(lng),
            address: address || 'Completion Location'
        });
        setShowLocationModal(true);
    };
    
    const handleCloseLocationModal = () => {
        setShowLocationModal(false);
        setSelectedLocation(null);
    };


    // Drag and Drop ==============================================================================================================

    const assignDriver = async (driver, routeUuid) => {
        try {
            if (!canEdit || isRouteLocked(selectedDate)) {
                showAlert({ message: 'Routing is locked or you do not have permission to assign drivers.', type: 'warning', buttons: [{ label: 'Close' }] });
                return;
            }
            if (isUserOnHoliday(driver.id, selectedDate)) {
                showAlert({ message: `${driver.name} ${driver.surname} is on holiday and cannot be assigned to routes.`, type: 'warning', buttons: [{ label: 'Close' }] });
                return;
            }
            const route = routeData.find(candidate => candidate.route_uuid === routeUuid
                && format(new Date(candidate.date), 'yyyy-MM-dd') === selectedDate);
            if (!route || route.is_subbed) throw new Error('The target route is no longer available for assignment.');
            await processDrop(driver, 'driver', route);
        } catch (error) {
            logger.error('Driver assignment failed:', error);
            showAlert({ message: 'Could not assign the driver. Please retry.', type: 'danger', buttons: [{ label: 'Close' }] });
        }
    };
    const { startDrag: startDriverDrag, preview: driverDragPreview } = useDriverPointerDrag(assignDriver, selectedDate);

    const handleDragStart = (e, type, item) => {
        try {
            writeRouteDragData(e.dataTransfer, type, item);
        } catch (error) {
            e.preventDefault();
            logger.error('Drag start failed:', error);
            showAlert({
                message: 'Could not start dragging. Use the selection dropdown on the route instead.',
                type: 'danger',
                buttons: [{ label: 'Close', type: 'danger' }],
            });
        }
    };


    const handleDrop = async (e, route, index) => {
        e.preventDefault();
        e.stopPropagation();
        e.dataTransfer.dropEffect = 'move';
        if (!canEdit || isRouteLocked(selectedDate)) return;
        
        try {
            const { type, item } = readRouteDragData(e.dataTransfer);
            if (type === 'allocated_route') {
                showAlert({
                    message: 'Drop the route onto an allocated job row to reorder or reassign it.',
                    type: 'warning',
                    buttons: [{ label: 'Close', type: 'warning' }],
                });
                return;
            }
            if (type === 'driver') {
                await assignDriver(item, route.route_uuid);
            } else {
                await processDrop(item, type, route);
            }
        } catch (error) {
            logger.error('Drop handling failed:', error);
            showAlert({
                message: 'Could not assign the dropped item. Please retry or use the selection dropdown on the route.',
                type: 'danger',
                buttons: [{ label: 'Close', type: 'danger' }],
            });
        }
    };

    const processDrop = async (data, type, route) => {

        // Call the function to update the route with the driver
        if (type === "driver") {
            const existingDropNumbers = routeData
                .filter((candidate) => (
                    candidate.route_uuid !== route.route_uuid
                    && candidate.profile_id === data.id
                    && format(new Date(candidate.date), "yyyy-MM-dd") === selectedDate
                    && Number.isFinite(Number(candidate.drop_number))
                ))
                .map((candidate) => Number(candidate.drop_number));
            const localDropNumber = existingDropNumbers.length > 0
                ? Math.max(...existingDropNumbers) + 1
                : 1;
            const nextDropResponse = await get_next_drop_number(data.id, selectedDate);
            const drop_number = nextDropResponse.status === 200
                ? nextDropResponse.drop_number
                : localDropNumber;
            await updateRouteData(route.route_uuid, {
                profile_id: data.id,
                driver: `${data.name} ${data.surname}`,
                drop_number: drop_number,
                truck_id: data.truck_id ?? null,
                truck_reg: vehicles.find((vehicle) => vehicle.id === data.truck_id)?.reg ?? null,
                truck_type: vehicles.find((vehicle) => vehicle.id === data.truck_id)?.type ?? null,
            });
        } else if (type === "trailer" && data.id !== 1) {
            await updateRouteData(route.route_uuid, {
                trailer_id: data.id,
                trailer_reg: data.reg,
                trailer_type: data.type
            });
        } else if (type === "truck") {
            await updateRouteData(route.route_uuid, {
                truck_id: data.id,
                truck_reg: data.reg,
                truck_type: data.type
            });
        } else {
            setSelectedRouteUuid(route.route_uuid);
            setOpenAddTrailerReg(true);
        }
    };
    
    const handleDragOver = allowRouteDrop;
    
    const updateRouteData = async (route_uuid, update) => {
        // Generate timestamp and update sync status immediately
        const timestamp = updateSyncTimestamp();
        
        // Add timestamp to the update data
        const updateWithTimestamp = {
            ...update,
            updated_at: timestamp
        };

        // update the database
        const { status } = await update_route(updateWithTimestamp, route_uuid)
        if (status !== 200) {
            showAlert({
                message: 'Failed to update route.',
                type: 'danger',
                buttons: [{
                    type: 'danger',
                    label: 'Close',
                }],
            });
        } else {
            // Update the state
            setRouteData((prevData) =>
                prevData.map((route) =>
                    route.route_uuid === route_uuid
                        ? { ...route, ...update }
                        : route
                )
            );
        }
        // reset the selected route id
        setSelectedRouteUuid(null);
    };

    // Handle subcontracting ========================================================================================================
    const contractOut = async (route_uuid, subContract) => {
        // subContract === false, (set is_subbed flase and reset subbed fields)
        const timestamp = updateSyncTimestamp();
        if (!subContract) {
            const { message, status } = await unSubcontract(route_uuid, timestamp)
            if (status !== 200) {
                showAlert({
                    message: 'Failed to un-subcontract route.',
                    type: 'danger',
                    buttons: [{
                        type: 'danger',
                        label: 'Close',
                    }],
                });
            } else {
                // update state
                setRouteData((prevData) =>
                    prevData.map((route) =>
                        route.route_uuid === route_uuid
                            ? { ...route, is_subbed: false, subbed_company: null, subbed_price: null, subbed_ref: null, updated_at: timestamp }
                            : route
                    )
                );
            }

        } else {
            // Open modal to input subcontractor details
            setSubEditInitialValues(null);
            setSelectedRouteUuid(route_uuid);
            setOpenSubContractModal(true);
        }
    };

    // Edit an existing subcontracted route
    const editSubcontractRoute = (route_uuid) => {
        const route = routeData.find(r => r.route_uuid === route_uuid);
        if (!route) return;
        setSubEditInitialValues({
            company: route.subbed_company || '',
            price: route.subbed_price != null ? String(route.subbed_price) : '',
            ref: route.subbed_ref || '',
        });
        setSelectedRouteUuid(route_uuid);
        setOpenSubContractModal(true);
    };

    // Subcontract modal state
    const [openSubContractModal, setOpenSubContractModal] = useState(false);
    const [openMtwModal, setOpenMtwModal] = useState(false);
    const [pendingSubcontractData, setPendingSubcontractData] = useState(null);
    const [subEditInitialValues, setSubEditInitialValues] = useState(null);

    // Submit handler - now shows MTW confirmation
    const handleSubContractSubmit = async (company, price, ref) => {
        // Store the subcontract data and show MTW confirmation modal
        const formattedFields = {
            company: company.trim(),
            price: String(Number(price).toFixed(2)),
            ref: ref.trim().toUpperCase(),
        };
        
        setPendingSubcontractData(formattedFields);
        setOpenSubContractModal(false);
        setOpenMtwModal(true);
    };

    // Handle MTW selection and final submission
    const handleMtwSelection = async (isMTW) => {
        const timestamp = updateSyncTimestamp();
        const finalData = {
            ...pendingSubcontractData,
            isMTW: isMTW
        };

        // Call API to subcontract with MTW flag
        const { status } = await subcontractRoute(selectedRouteUuid, finalData, timestamp);
        if (status !== 200) {
            showAlert({
                message: 'Failed to subcontract route.',
                type: 'danger',
                buttons: [{ type: 'danger', label: 'Close' }],
            });
        } else {
            setRouteData((prevData) =>
                prevData.map((route) =>
                    route.route_uuid === selectedRouteUuid
                        ? {
                            ...route,
                            is_subbed: true,
                            subbed_company: finalData.company,
                            subbed_price: finalData.price,
                            subbed_ref: finalData.ref,
                            isMTW: isMTW,
                            profile_id: null,
                            driver: null,
                            truck_id: null,
                            truck_reg: null,
                            truck_type: null,
                            trailer_id: null,
                            trailer_reg: null,
                            trailer_type: null,
                            updated_at: timestamp
                        }
                        : route
                )
            );
            setOpenMtwModal(false);
            setSelectedRouteUuid(null);
            setPendingSubcontractData(null);
            setSubEditInitialValues(null);
        }
    };

    // Add driver or vehilce manually from dropdown =================================================================================
    const handleAddFromDropdown = (route_uuid, type) => {
        setSelectedRouteUuid(route_uuid);
        setDropdownType(type);
        setOpenSelectFromDropdown(true);
    };

    const addFromDropdown = async (value) => {
        if (dropdownType === 'driver') {
            await assignDriver(value, selectedRouteUuid);
        } else if (dropdownType === 'truck') {
            updateRouteData(selectedRouteUuid, {
                truck_id: value.id,
                truck_reg: value.reg,
                truck_type: value.type
            });
        } else if (dropdownType === "trailer" && value.id !== 1) {
            updateRouteData(selectedRouteUuid, {
                trailer_id: value.id,
                trailer_reg: value.reg,
                trailer_type: value.type
            });
        };
        
        // reset the dropdown
        setSelectedRouteUuid(null);
        setDropdownType(null);
        setOpenSelectFromDropdown(false);
    };

    // Download CSV ==============================================================================================================
    const handleDownload = async (data) => {
        const {status} = await downloadRoute(data);

        if (status !== 200) {
            showAlert({
                message: 'Failed to download route, try refreshing the page !!',
                type: 'danger',
                buttons: [{
                    type: 'primary',
                    label: 'Close',
                }],
            });
        }
    };

    // Hanlde Notes ==============================================================================================================

    const handleUpdateNotes = async () => {
        if (!todaysNotes.notes && !todaysNotes.date) return;

        if (todaysNotes.date && !todaysNotes.notes) {
            // if notes are empty, delete the note
            const { status } = await delete_notes(todaysNotes.date);
            if (status !== 200) {
                showAlert({
                    message: 'Failed to delete note, try refreshing the page !!',
                    type: 'danger',
                    buttons: [{
                        type: 'danger',
                        label: 'Close',
                    }],
                });
            } else {
                // update the notes state
                setNotesData((prev) => {
                    const updatedNotes = prev.filter((note) => note.date !== todaysNotes.date);
                    return updatedNotes;
                });
            }
        } else if (todaysNotes.date === null) {
            const newNote = {
                date: selectedDate,
                notes: todaysNotes.notes,
            }

            // Insert the new note into the database
            const { status } = await add_notes(newNote);
            if (status !== 200) {
                showAlert({
                    message: 'Failed to add note to database.',
                    type: 'danger',
                    buttons: [{
                        type: 'danger',
                        label: 'Close',
                    }],
                });
            } else {
                // update the date for todays notes (shows that an event has been added succesfully, now should only update the notes)
                setTodaysNotes((prev) => ({
                    ...prev,
                    date: selectedDate,
                }));
                // update notes state
                setNotesData((prev) => {
                    const newNotes = [...prev];
                    newNotes.push(newNote);
                    return newNotes;
                });
            }
        } else {
            // Update the existing note
            const updatedNote = {
                date: todaysNotes.date,
                notes: todaysNotes.notes,
            };
            // Update the note in the database
            const { status } = await update_notes(updatedNote);
            if (status !== 200) {
                showAlert({
                    message: 'Failed to update note in database.',
                    type: 'danger',
                    buttons: [{
                        type: 'danger',
                        label: 'Close',
                    }],
                });
            } else {
                // update notes state
                setNotesData((prev) => {
                    const updatedNotes = prev.map((note) => {
                        if (note.date === selectedDate) {
                            return { ...note, notes: todaysNotes.notes };
                        }
                        return note;
                    });
                    return updatedNotes;
                });
            }
        }
    }


    // Generate the calendar ========================================================================================================
    const generateCalendar = () => {
        const startDate = startOfWeek(startOfMonth(currentDate));
        const endDate = endOfWeek(endOfMonth(currentDate));
        const days = [];
        let day = startDate;

        while (day <= endDate) {
            const formattedDate = format(day, "yyyy-MM-dd");
            const isCurrentMonth = day.getMonth() === currentDate.getMonth();
            const dayEvents = routeData?.filter((event) => format(new Date(event.date), "yyyy-MM-dd") === formattedDate) ?? [];
            const pendingRequestsForDay = monthlyPendingRequests.filter((req) => req.date === formattedDate && req.status === 'pending');
            const pendingCount = pendingRequestsForDay.length;
            
            days.push(
                <motion.div
                    key={formattedDate}
                    className={`day ${isCurrentMonth ? "current-month" : "other-month"} ${
                        isToday(day) ? "today" : ""
                    }`}
                    onClick={() => setSelectedDate(formattedDate)}
                    whileHover={{ scale: 1.05 }}
                    style={{
                        height: '12.5vh',
                        zIndex: 0,
                        borderColor: isLocked(dayEvents) 
                            ? 'rgba(252, 23, 23, 0.84)' // Color for when the route is locked
                            : null,
                        borderWidth: isLocked(dayEvents) ? 3 : 1,
                        // backgroundColor: isLocked(dayEvents)
                        //     ? 'rgba(5, 253, 18, 0.2)'
                        //     : null,
                    }}
                >
                    <div className="date">{format(day, "d")}</div>
                    
                    {pendingCount > 0 && (
                        <div style={{
                            position: 'absolute',
                            top: '5px',
                            right: '5px',
                            backgroundColor: '#fc1717',
                            borderRadius: '50%',
                            width: '20px',
                            height: '20px',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            zIndex: 10,
                            boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                        }}>
                            <span style={{
                                color: '#fff',
                                fontSize: '11px',
                                fontWeight: 'bold',
                                lineHeight: 1
                            }}>{pendingCount}</span>
                        </div>
                    )}

                    <div className="event-container"
                        style={{
                            height: '100%',
                            overflowY: 'auto',
                            padding: 5,
                            paddingBottom: 25,
                            borderRadius: 5,
                        }}
                    >
                    {dayEvents.length > 0 && (
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            <FontAwesomeIcon
                                style={{
                                    width: 40,
                                    fontSize: '2.5rem',
                                    cursor: 'crosshair',
                                }}
                                icon={faRoute}
                            />
                        </div>
                    )}
                    </div>
                </motion.div>
            );
            day = addDays(day, 1);
        }
        return days;
    };

    // handle change month ==========================================================================================================
    const changeMonth = (newDate) => {
        setCurrentDate(newDate);
    };


    // Render the day view when a date is selected ========================================================================================================
    const renderDayView = () => {
        if (!selectedDate) return null; // If no date is selected, don't render anything
    
        // Filter events for the selected date
        const dayEvents = routeData.filter((event) => format(new Date(event.date), "yyyy-MM-dd") === selectedDate);
        const networkDriverIds = new Set(
            dayEvents
                .filter((event) => event.is_network_freight && event.profile_id)
                .map((event) => String(event.profile_id))
        );
        const belongsInNetworkSection = (event) =>
            event.is_network_freight || (event.profile_id && networkDriverIds.has(String(event.profile_id)));
        const standardDayEvents = dayEvents.filter((event) => !belongsInNetworkSection(event));
        const networkDayEvents = dayEvents.filter(belongsInNetworkSection);

        const renderRouteGroup = (routes, key) => (
            <RouteContainer
                key={key}
                routes={routes}
                drivers={drivers ?? []}
                vehicles={vehicles ?? []}
                vehicleServiceList={vehiclesServiceList}
                vehicleOverdueList={vehiclesOverdueList}
                vorList={vorList}
                holidaysData={holidaysData}
                selectedDate={selectedDate}
                isUserOnHoliday={(userId) => isUserOnHoliday(userId, selectedDate)}
                deleteRoute={(route_uuid) => handleDeleteRoute(route_uuid)}
                removeDriver={(route_uuid) => handleRemoveDriver(route_uuid)}
                removeVehicle={(route_uuid, type) => handleRemoveVehicle(route_uuid, type)}
                theme={theme}
                onDragStart={handleDragStart}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                onAssignDriver={assignDriver}
                showJobComments={(route_uuid, job_comments) => handleToggleJobComment(route_uuid, job_comments)}
                showJobCostings={(route_uuid, costings) => handleToggleJobCostings(route_uuid, costings)}
                showInvoiceTo={(route_uuid, invoice_id) => handleToggleInvoiceTo(route_uuid, invoice_id)}
                showDemurrage={(route_uuid) => handleToggleDemurrage(route_uuid)}
                isLocked={isRouteLocked(selectedDate)}
                showCostings={showCostings}
                showStartTime={(route_uuid, start_time) => handleToggleStartTime(route_uuid, start_time)}
                showAddFromDropdown={(route_uuid, type) => handleAddFromDropdown(route_uuid, type)}
                toggleConfirmed={(route_uuid, confirmed) => toggleRouteConfirmed(route_uuid, confirmed)}
                onConfirmAllForDriver={(driverRoutes) => confirmAllForDriver(driverRoutes)}
                onUnconfirmAllForDriver={(driverRoutes) => unconfirmAllForDriver(driverRoutes)}
                contractRoute={(route_uuid, subContract) => contractOut(route_uuid, subContract)}
                canEdit={canEdit}
                editSubcontract={(route_uuid) => editSubcontractRoute(route_uuid)}
                completedRouteUuids={completedRouteUuids}
                onViewCompletion={(route_uuid, routeInfo) => handleViewCompletion(route_uuid, routeInfo)}
                showDeliveryDetails={(route_uuid) => handleOpenDeliveryDetails(route_uuid)}
                deliveryAlerts={deliveryAlerts}
                onPodOff={(route_uuid) => handlePodOff(route_uuid)}
                moveRoute={(route_uuid, newDate) => handleMoveRoute(route_uuid, newDate)}
                onReassignDriver={(route_uuid, update) => {
                    setRouteData((prev) => prev.map((route) => route.route_uuid === route_uuid ? { ...route, ...update } : route));
                }}
                classicView={useClassicRouteView}
            />
        );
    
        return (
            <motion.div
                className="modal"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.3 }}
            >

                {/* Open dropdown select (backup to select from drivers or vehicles) */}
                {openSelectFromDropdown && (
                    <SelectDropdown
                        theme={theme}
                        type={dropdownType}
                        header={dropdownType === 'driver' ? 'Select Driver' : 'Select Vehicle'}
                        inputTag={dropdownType === 'driver' ? 'Driver' : 'Vehicle'}
                        isOpen={openSelectFromDropdown}
                        setIsOpen={setOpenSelectFromDropdown}
                        list={dropdownType === 'driver' ? drivers : vehicles}
                        returnData={(value) => {addFromDropdown(value)}}
                    />
                )}

                {/* List all drivers, show if route not locked */}
                {!isRouteLocked(selectedDate) && (
                    <div className={`modal-drivers-notes-container${sidebarCollapsed ? ' sidebar-collapsed' : ''}`}>
                        {/* Collapsed tab — expand button */}
                        {sidebarCollapsed && (
                            <div
                                className="sidebar-expand-tab"
                                style={{
                                    background: theme === 'dark' ? '#080808' : '#fff',
                                    border: theme === 'dark' ? '5px solid #fff' : '5px solid #000',
                                }}
                                onClick={() => setSidebarCollapsed(false)}
                                title="Expand sidebar"
                            >
                                <FontAwesomeIcon icon={faAnglesRight} style={{ width: 18 }} />
                            </div>
                        )}
                        {/* Sidebar content — hidden when collapsed */}
                        {!sidebarCollapsed && (
                        <>
                        <div className="modal-drivers"
                            style={{
                                border: (isRouteLocked(selectedDate) || !canEdit) 
                                    ? '5px solid rgba(247, 13, 5, 0.8)' 
                                    : theme === 'dark' ? '5px solid #fff' : '5px solid #000',
                                    backgroundColor: (isRouteLocked(selectedDate) || !canEdit)
                                        ? 'rgba(247, 13, 5, 0.2)'
                                        : theme === 'dark' ? '#080808' : '#ffffff',
                            }}
                        >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                                <h2 style={{ margin: 0 }}>Drivers</h2>
                                <button
                                    title="Collapse sidebar"
                                    onClick={() => setSidebarCollapsed(true)}
                                    style={{
                                        background: 'transparent',
                                        border: 'none',
                                        cursor: 'pointer',
                                        color: theme === 'dark' ? '#fff' : '#333',
                                        fontSize: 18,
                                        padding: '4px 6px',
                                        borderRadius: 4,
                                        lineHeight: 1,
                                    }}
                                >
                                    <FontAwesomeIcon icon={faAnglesLeft} style={{ color: theme === 'dark' ? '#fff' : '#333', width: 18 }} />
                                </button>
                            </div>

                            {/* list */}
                            {drivers.length > 0 ? (
                                drivers.map((driver, index) => (
                                    <span
                                        className="driver-container"
                                        style={{
                                            cursor: (isRouteLocked(selectedDate) || !canEdit || isUserOnHoliday(driver.id, selectedDate)) ? 'not-allowed' : 'grab',
                                            textAlign: 'center',
                                            backgroundColor: isDriverAssigned(driver.id) ? 'rgba(26, 221, 247, 0.6)' : theme === 'dark' ? '#333' : '#fff',
                                            border: isUserOnHoliday(driver.id, selectedDate) ? '2px solid yellow' : '1px solid #ccc',
                                            opacity: isUserOnHoliday(driver.id, selectedDate) ? 0.6 : 1,
                                        }}
                                        key={driver.id}
                                        title={isRouteLocked(selectedDate)
                                            ? 'This route is locked. Unlock it before assigning drivers.'
                                            : !canEdit
                                                ? 'You do not have permission to edit routing.'
                                                : isUserOnHoliday(driver.id, selectedDate)
                                                    ? `${driver.name} ${driver.surname} is on holiday.`
                                                    : `Drag ${driver.name} ${driver.surname} onto a job.`}
                                        draggable={false}
                                        onDragStart={(e) => e.preventDefault()}
                                        onPointerDown={(e) => {
                                            if (canEdit && !isRouteLocked(selectedDate) && !isUserOnHoliday(driver.id, selectedDate)) {
                                                startDriverDrag(e, driver);
                                            }
                                        }}
                                    >
                                        {driver.name} {driver.surname}
                                    </span>
                                ))
                            ) : (
                                <p>No drivers available</p>
                            )}
                        </div>
                        {driverDragPreview && (
                            <div className="driver-drag-preview" style={{ left: driverDragPreview.x + 12, top: driverDragPreview.y + 12 }}>
                                {driverDragPreview.driver.name} {driverDragPreview.driver.surname}
                            </div>
                        )}
                        <div className="modal-notes">
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span style={{ fontSize: '28', textDecoration: 'underline', fontStyle: 'italic', fontWeight: 'bold' }}>Notes</span>
                                {canEdit && (
                                    <span
                                        className="hoverable"
                                        style={{
                                            fontSize: 12,
                                            color: 'gray',
                                            cursor: 'context-menu',
                                        }}
                                        onClick={() => handleUpdateNotes()}
                                    >
                                        <FontAwesomeIcon icon={faCheckCircle} style={{ width: 20, fontSize: 20, color: '#27e310' }} />
                                    </span>
                                )}
                            </div>

                            {todaysNotes !== null && (
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                    <textarea
                                    disabled={!canEdit}
                                    style={{
                                        width: '100%',
                                        height: '14vh',
                                        backgroundColor: theme === 'dark' ? '#333' : '#fff',
                                        color: theme === 'dark' ? '#fff' : '#000',
                                        padding: 10,
                                        borderRadius: 5,
                                        border: '1px solid #ccc',
                                        marginTop: 5,
                                        resize: 'none',
                                        fontStyle: 'italic',
                                        opacity: canEdit ? 1 : 0.8
                                    }}
                                    value={todaysNotes.notes ?? ''}
                                    onChange={(e) =>
                                        setTodaysNotes((prev) => ({
                                        ...prev,
                                        notes: e.target.value,
                                        }))
                                    }
                                    placeholder="Add notes for this date"
                                    />
                                </div>
                            )}

                        </div>
                        </>
                        )}
                    </div>
                )}

                {/* List all jobs */}
                <div
                    className="modal-content"
                    style={{
                        border: isRouteLocked(selectedDate) 
                            ? '5px solid rgba(247, 13, 5, 0.8)' 
                            : theme === 'dark' ? '5px solid #fff' : '5px solid #000',
                            backgroundColor: theme === 'dark' ? '#080808' : '#fff',
                            width: (isRouteLocked(selectedDate) || sidebarCollapsed) ? '100%' : null,
                    }}
                >
                    {/* Subcontractor Modal using SingleValueInput style */}
                    {openSubContractModal && (
                        <SubcontractorInput
                            theme={theme}
                            header="Subcontractor Details"
                            isOpen={openSubContractModal}
                            setIsOpen={setOpenSubContractModal}
                            handleSubmit={(company, price, ref) => {handleSubContractSubmit(company, price, ref)}}
                            initialValues={subEditInitialValues}
                            onCancel={() => { setOpenSubContractModal(false); setSelectedRouteUuid(null); setSubEditInitialValues(null); }}
                            savedSubcontractors={savedSubcontractors}
                        />
                    )}

                    {/* MTW Confirmation Modal */}
                    {openMtwModal && (
                        <div className="modal-overlay d-flex justify-content-center align-items-center" style={{
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            backgroundColor: 'rgba(0, 0, 0, 0.5)',
                            zIndex: 9999
                        }}>
                            <div className="modal-dialog modal-dialog-centered">
                                <div className="modal-content" style={{
                                    backgroundColor: theme === 'dark' ? '#1a1a1a' : '#fff',
                                    border: theme === 'dark' ? '1px solid #444' : '1px solid #ccc',
                                    borderRadius: '8px'
                                }}>
                                    <div className="modal-header" style={{
                                        borderBottom: theme === 'dark' ? '1px solid #444' : '1px solid #dee2e6',
                                        backgroundColor: theme === 'dark' ? '#2a2a2a' : '#f8f9fa'
                                    }}>
                                        <h5 className="modal-title" style={{
                                            color: theme === 'dark' ? '#fff' : '#000',
                                            margin: 0
                                        }}>
                                            MTW Contractor
                                        </h5>
                                    </div>
                                    <div className="modal-body text-center" style={{
                                        color: theme === 'dark' ? '#fff' : '#000',
                                        padding: '2rem'
                                    }}>
                                        <p style={{ fontSize: '1.1em', marginBottom: '1.5rem' }}>
                                            Is this job being subcontracted through MTW?
                                        </p>
                                        <div className="d-flex justify-content-center gap-3">
                                            <button 
                                                className="btn btn-success px-4 py-2" 
                                                onClick={() => handleMtwSelection(true)}
                                                style={{ minWidth: '80px' }}
                                            >
                                                Yes
                                            </button>
                                            <button 
                                                className="btn btn-secondary px-4 py-2" 
                                                onClick={() => handleMtwSelection(false)}
                                                style={{ minWidth: '80px' }}
                                            >
                                                No
                                            </button>
                                        </div>
                                        <div className="mt-3">
                                            <button 
                                                className="btn btn-outline-secondary" 
                                                onClick={() => {
                                                    setOpenMtwModal(false);
                                                    setOpenSubContractModal(true);
                                                    setPendingSubcontractData(null);
                                                }}
                                            >
                                                Back to Details
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Start Time Modal */}
                    <StartTimeModal
                        isOpen={openStartTimeModal}
                        onClose={() => setOpenStartTimeModal(false)}
                        onSave={saveStartTime}
                        currentTime={currentStartTime}
                        theme={theme}
                    />
                    {/* Top bar */}
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: 20,
                            padding: '0px 20px 0px 20px',
                        }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
                            <span style={{ fontSize: 24, color: theme === 'dark' ? '#fff' : '#000' }}>
                                {format(new Date(selectedDate), "EEE, MMMM dd")}
                            </span>
                            {/* Toggle switch */}
                            <div
                                onClick={() => setUseClassicRouteView((v) => !v)}
                                title={useClassicRouteView ? 'Switch to Allocation View' : 'Switch to Route View'}
                                style={{
                                    position: 'relative',
                                    width: 40,
                                    height: 22,
                                    borderRadius: 11,
                                    background: useClassicRouteView ? '#6366f1' : (theme === 'dark' ? '#555' : '#ccc'),
                                    cursor: 'pointer',
                                    transition: 'background 0.2s',
                                    flexShrink: 0,
                                }}
                            >
                                <div style={{
                                    position: 'absolute',
                                    top: 3,
                                    left: useClassicRouteView ? 21 : 3,
                                    width: 16,
                                    height: 16,
                                    borderRadius: '50%',
                                    background: '#fff',
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                                    transition: 'left 0.2s',
                                }} />
                            </div>
                            <span style={{ fontSize: 12, color: theme === 'dark' ? '#aaa' : '#666', whiteSpace: 'nowrap' }}>
                                {useClassicRouteView ? 'Route View' : 'Allocation View'}
                            </span>
                        </div>

                        {/* Add new job button */}
                        {canEdit && (
                        <span
                            className="add-event-button"
                            style={{ cursor: isRouteLocked(selectedDate) ? 'context-menu' : 'pointer' }}
                            onClick={() => {
                                if (isRouteLocked(selectedDate)) {
                                    handleDownload(dayEvents);
                                } else {
                                    handleSelectJob();
                                }
                            }}
                        >
                            {isRouteLocked(selectedDate) ? 'Download CSV' : '+ Add Job'}
                        </span>
                        )}

                        {features?.invoicing !== false && canInvoice && isRouteLocked(selectedDate) && (
                        <span
                            className="add-event-button"
                            style={{
                                cursor: isGeneratingInvoiceDrafts ? 'wait' : 'pointer',
                                opacity: isGeneratingInvoiceDrafts ? 0.6 : 1,
                            }}
                            onClick={isGeneratingInvoiceDrafts ? undefined : handleGenerateInvoiceDrafts}
                        >
                            {isGeneratingInvoiceDrafts ? 'Generating invoices...' : 'Generate invoice drafts'}
                        </span>
                        )}

                        {/* Request job button for view-only users */}
                        {!canEdit && (
                        <span
                            className="add-event-button"
                            style={{ cursor: isRouteLocked(selectedDate) ? 'context-menu' : 'pointer' }}
                            onClick={() => {
                                if (isRouteLocked(selectedDate)) {
                                    handleDownload(dayEvents);
                                } else {
                                    setOpenRequestJob(true);
                                }
                            }}
                        >
                            {isRouteLocked(selectedDate) ? 'Download CSV' : '+ Request Job'}
                        </span>
                        )}

                        {/* Show pricing */}
                        {canEdit && (
                        <span
                            className="add-event-button"
                            onClick={async () => {
                                if (!showCostings) {
                                    verifyPassword('costings');
                                } else {
                                    toggleShowCostings(); // Directly hide costings without verification
                                }
                            }}
                        >
                            {showCostings ? 'Hide Costing' : 'Show Costing'}
                        </span>
                        )}

                        {/* Print PW */}
                        {canEdit && (
                        <span
                            className="add-event-button"
                            onClick={() => {
                                setShowPrintPWModal(true);
                                setSelectedRoutesToPrint([]);
                            }}
                        >
                            Print PW
                        </span>
                        )}

                        {/* Delivery Notes */}
                        <span
                            className="add-event-button"
                            onClick={() => {
                                setShowDeliveryNotesModal(true);
                                setSelectedDeliveryNoteRoutes([]);
                            }}
                        >
                            Delivery Notes
                        </span>

                        {/* LOCK IN ROUTE */}
                        {canEdit && (!isRouteLocked(selectedDate) || canUnlock) && (
                        <span
                            className="add-event-button"
                            onClick={async () => {
                                if (isRouteLocked(selectedDate)) {
                                    verifyPassword('toggleRouteLock');
                                } else {
                                    toggleRouteLock(); // Directly lock/unlock the route without verification
                                }
                            }}
                        >
                            {isRouteLocked(selectedDate) ? 'Unlock Route' : 'Lock Route'}
                        </span>
                        )}



                        {/* Close button */}
                        <button
                            className="btn btn-secondary"
                            style={{ marginTop: '0px', width: 115 }}
                            onClick={() => setSelectedDate(null)}
                        >
                            Close
                        </button>
                    </div>

                    {/* Job Requests Table (Above the main grids) */}
                    <JobRequestsTable
                        theme={theme}
                        requests={routingRequests}
                        canEdit={canEdit}
                        onAccept={handleAcceptRequest}
                        onDecline={handleDeclineRequest}
                        onCancel={handleCancelRequest}
                        currentUserId={profile?.id}
                    />

                    {/* daily routes list */}
                    {dayEvents.length > 0 ? (
                        <>
                            {standardDayEvents.length > 0 && renderRouteGroup(standardDayEvents, 'standard-routes')}
                            {networkDayEvents.length > 0 && (
                                <section className="network-freight-routes">
                                    <h3>Network Freight</h3>
                                    {renderRouteGroup(networkDayEvents, 'network-routes')}
                                </section>
                            )}
                        </>
                    ) : (
                        <h5 style={{ display: 'flex', justifyContent: 'center', marginTop: '50px'}}>No routes for this date</h5>
                    )}
                </div>

                {/* List all vehicles */}
                {/* {!isRouteLocked(selectedDate) && (
                    <div className="modal-vehicles"
                        style={{
                            border: isRouteLocked(selectedDate) 
                                ? '5px solid rgba(247, 13, 5, 0.8)' 
                                : theme === 'dark' ? '5px solid #fff' : '5px solid #000',
                                backgroundColor: isRouteLocked(selectedDate)
                                    ? 'rgba(247, 13, 5, 0.2)'
                                    : theme === 'dark' ? '#080808' : '#fff',
                        }}
                    >
                        <h2>Vehicles</h2>
        
                        {vehicles.length > 0 ? (
                            [...vehicles, { id: 1, reg: "** External **", type: 'TRAILER' }].map((vehicle, index) => {

                                return (
                                    <span
                                        className="driver-container"
                                        style={{
                                            cursor: isRouteLocked(selectedDate) ? 'not-allowed' : 'grab',
                                            textAlign: 'center',
                                        }}
                                        key={vehicle.id}
                                        draggable={!isRouteLocked(selectedDate)}
                                        onDragStart={(e) => {
                                            if (!isRouteLocked(selectedDate)) {
                                                handleDragStart(e, vehicle.type === 'TRAILER' ? "trailer" : "truck", vehicle);
                                            } else {
                                                e.preventDefault();
                                            }
                                        }}
                                        onDragEnd={(e) => {
                                            // Reset cursor after drag operation
                                            e.target.style.cursor = isRouteLocked(selectedDate) ? 'not-allowed' : 'grab';
                                        }}
                                    >
                                        {vehicle.reg}
                                    </span>
                                );
                            })
                        ) : (
                            <p>No vehicles available</p>
                        )}

                    </div>
                )} */}
            </motion.div>
        );
    };
    

    return (
        <div id={theme} className="calendar" style={{ backgroundColor: theme === 'dark' ? '#080808' : '#fff' }}>
            
            {/* Route Sync Banner */}
            {showSyncBanner && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    backgroundColor: '#ffc107f4',
                    color: '#000',
                    padding: '10px',
                    textAlign: 'center',
                    zIndex: 1050,
                    borderBottom: '2px solid #e0a800'
                }}>
                    <strong>Routes have changed!</strong> Please sync to get the latest data.
                    <Button 
                        variant="dark" 
                        size="sm" 
                        onClick={handleSync}
                        disabled={isPolling}
                        style={{ marginLeft: '15px' }}
                    >
                        {isPolling ? 'Syncing...' : 'Sync Now'}
                    </Button>
                    <Button 
                        variant="link" 
                        size="sm" 
                        onClick={() => setShowSyncBanner(false)}
                        style={{ marginLeft: '10px', color: '#000' }}
                    >
                        Dismiss
                    </Button>
                </div>
            )}
            
            {/* Progress Tracker for bulk operations */}
            {isBulkProcessing && (
                <ProgressTracker
                    show={isBulkProcessing}
                    progress={bulkProgress}
                    status={bulkStatus}
                    theme={theme}
                />
            )}
            
            {/* Show loading spinner */}
            {isLoading && (
                <div>
                    <ActivityIndicator size={50} />
                </div>
            )}

            {/* Render the RequestPassword modal only if openRequestPassword is true */}
            {openRequestPassword && (
                <RequestPassword
                    theme={theme}
                    isOpen={openRequestPassword}
                    setIsOpen={setOpenRequestPassword}
                    returnData={(value) => handlePasswordSubmission(value)}
                />
            )}

            {/* select job modal */}
            {openSelectJob && (
                <SelectJobModal
                    theme={theme}
                    openSelectJob={openSelectJob}
                    setOpenSelectJob={setOpenSelectJob}
                    customers={customers}
                    customerAccounts={customerAccounts}
                    addressBook={addressBook}
                    addJob={addJob}
                    addBulkJobs={addBulkJobs}
                    date={selectedDate}
                    routes={routeData}
                    syncJobs={syncJobs}
                    canSaveAddress={canSaveAddress}
                    onAddressSaved={handleAddressSaved}
                            onNetworkFreightUploaded={(createdJobs) => {
                                setRouteData((previous) => [...previous, ...createdJobs]);
                                showAlert({
                                    message: `${createdJobs.length} network freight job${createdJobs.length === 1 ? '' : 's'} uploaded.`,
                                    type: 'success',
                                    buttons: [{ type: 'primary', label: 'Close' }],
                                });
                            }}
                />
            )}

            {/* request job modal */}
            {openRequestJob && (
                <RequestJobModal
                    theme={theme}
                    openRequestJob={openRequestJob}
                    setOpenRequestJob={setOpenRequestJob}
                    customers={customers}
                    addressBook={addressBook}
                    date={selectedDate}
                    onSubmitSuccess={() => loadRoutingRequests(selectedDate)}
                    canSaveAddress={canSaveAddress}
                    onAddressSaved={handleAddressSaved}
                />
            )}

            {/* show job comments input */}
            {openJobComments && (
                <JobComments
                    theme={theme}
                    openJobComments={openJobComments}
                    setOpenJobComments={setOpenJobComments}
                    route_uuid={selectedRouteUuid}
                    job_comments={selectedJobComments}
                    add_comments={addNewComments}
                />
            )}

            {/* Delivery details modal */}
            {showDeliveryDetailsModal && (
                <DeliveryDetailsModal
                    show={showDeliveryDetailsModal}
                    onClose={handleCloseDeliveryDetails}
                    route={deliveryDetailsRoute}
                    existingRecord={deliveryDetailsRecord}
                    isLoading={deliveryDetailsLoading}
                    theme={theme}
                    canEdit={canEdit && !isRouteLocked(selectedDate) && !deliveryDetailsRoute?.locked}
                />
            )}

            {/* show demurrage modal */}
            {openDemurrage && (
                <DemurrageModal
                    show={openDemurrage}
                    onClose={() => {
                        setOpenDemurrage(false);
                        setSelectedRouteUuid(null);
                        setSelectedDemurrageData(null);
                    }}
                    demurrageData={selectedDemurrageData}
                    onSave={saveDemurrage}
                    theme={theme}
                />
            )}

            {/* show demurrage details modal */}
            {openDemurrageDetails && (
                <DemurrageDetailsModal
                    theme={theme}
                    isOpen={openDemurrageDetails}
                    onClose={handleCloseDemurrageDetails}
                    onRemove={handleRemoveDemurrage}
                    demurrageData={demurrageDetailsData}
                />
            )}

            {/* Print PW Modal */}
            {showPrintPWModal && (
                <div className="modal-overlay" style={{
                    position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
                    background: 'rgba(0,0,0,0.7)', zIndex: 9999, display: 'flex', justifyContent: 'center', alignItems: 'center'
                }}>
                    <div style={{
                        background: theme === 'light' ? '#fff' : '#222',
                        color: theme === 'light' ? '#222' : '#fff',
                        borderRadius: 10,
                        border: theme === 'light' ? '1px solid #444' : '2px solid #ddd',
                        padding: 30,
                        minWidth: 600,
                        maxWidth: 800,
                        maxHeight: '80vh',
                        boxShadow: '0 2px 12px rgba(0,0,0,0.2)',
                        position: 'relative',
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden'
                    }}>
                        {/* Header */}
                        <div style={{ 
                            marginBottom: 20, 
                            paddingBottom: 15, 
                            borderBottom: `1px solid ${theme === 'light' ? '#eee' : '#444'}`,
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        }}>
                            <h3 style={{ margin: 0, fontSize: '1.5em' }}>
                                Print Paperwork - {selectedDate ? new Date(selectedDate).toLocaleDateString() : 'No Date'}
                            </h3>
                            <button 
                                onClick={() => {
                                    setShowPrintPWModal(false);
                                    setSelectedRoutesToPrint([]);
                                }}
                                style={{
                                    background: 'transparent',
                                    border: 'none',
                                    fontSize: '24px',
                                    cursor: 'pointer',
                                    color: theme === 'light' ? '#666' : '#ccc',
                                    padding: 0,
                                    width: '30px',
                                    height: '30px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                            >
                                ×
                            </button>
                        </div>
                        
                        {/* Body */}
                        <div style={{ flex: 1, overflow: 'auto' }}>
                            <div style={{ marginBottom: 20 }}>
                                <label style={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: 8, 
                                    fontWeight: 'bold',
                                    cursor: 'pointer',
                                    fontSize: '1.1em'
                                }}>
                                    <input
                                        type="checkbox"
                                        checked={selectedRoutesToPrint.length === currentDayRoutes.length && currentDayRoutes.length > 0}
                                        onChange={(e) => handleSelectAllRoutes(e.target.checked)}
                                        style={{ marginRight: 5 }}
                                    />
                                    Select All Routes ({currentDayRoutes.length})
                                </label>
                            </div>
                            
                            <div>
                                {currentDayRoutes.length === 0 ? (
                                    <p style={{ textAlign: 'center', color: theme === 'light' ? '#666' : '#aaa', fontSize: '1.1em' }}>
                                        No routes found for this date.
                                    </p>
                                ) : (
                                    currentDayRoutes.map((route) => (
                                        <div 
                                            key={route.route_uuid} 
                                            style={{ 
                                                padding: 15, 
                                                margin: '10px 0',
                                                border: `1px solid ${theme === 'light' ? '#ddd' : '#555'}`,
                                                borderRadius: 8,
                                                backgroundColor: selectedRoutesToPrint.includes(route.route_uuid) 
                                                    ? (theme === 'light' ? '#f0f8ff' : '#2c3e50') 
                                                    : 'transparent',
                                                transition: 'all 0.2s ease'
                                            }}
                                        >
                                            <label style={{ 
                                                display: 'flex', 
                                                alignItems: 'flex-start', 
                                                gap: 12, 
                                                cursor: 'pointer',
                                                width: '100%'
                                            }}>
                                                <input
                                                    type="checkbox"
                                                    checked={selectedRoutesToPrint.includes(route.route_uuid)}
                                                    onChange={(e) => handleRouteSelection(route.route_uuid, e.target.checked)}
                                                    style={{ marginTop: 2 }}
                                                />
                                                <div style={{ flex: 1 }}>
                                                    <div style={{ 
                                                        fontWeight: 'bold', 
                                                        marginBottom: 6,
                                                        fontSize: '1.1em'
                                                    }}>
                                                        {route.job} - {route.route || 'No route'}
                                                    </div>
                                                    <div style={{ 
                                                        fontSize: '0.95em', 
                                                        opacity: 0.8,
                                                        marginBottom: 4
                                                    }}>
                                                        Driver: {route.driver || 'Not assigned'}
                                                    </div>
                                                    <div style={{ 
                                                        fontSize: '0.85em', 
                                                        opacity: 0.7
                                                    }}>
                                                        {route.postcode} | {route.route_ref || 'No consignment'}
                                                    </div>
                                                </div>
                                            </label>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                        
                        {/* Footer */}
                        <div style={{ 
                            borderTop: `1px solid ${theme === 'light' ? '#eee' : '#444'}`,
                            paddingTop: 15,
                            marginTop: 15,
                            display: 'flex',
                            gap: 10,
                            justifyContent: 'flex-end'
                        }}>
                            <button 
                                onClick={() => {
                                    setShowPrintPWModal(false);
                                    setSelectedRoutesToPrint([]);
                                }}
                                style={{
                                    padding: '10px 20px',
                                    border: `1px solid ${theme === 'light' ? '#ccc' : '#555'}`,
                                    borderRadius: 6,
                                    background: 'transparent',
                                    color: theme === 'light' ? '#666' : '#ccc',
                                    cursor: 'pointer',
                                    fontSize: '1em'
                                }}
                            >
                                Cancel
                            </button>
                            <button 
                                onClick={handlePrintPW}
                                disabled={selectedRoutesToPrint.length === 0}
                                style={{
                                    padding: '10px 20px',
                                    border: 'none',
                                    borderRadius: 6,
                                    background: selectedRoutesToPrint.length === 0 ? '#ccc' : '#007bff',
                                    color: '#fff',
                                    cursor: selectedRoutesToPrint.length === 0 ? 'not-allowed' : 'pointer',
                                    fontSize: '1em',
                                    opacity: selectedRoutesToPrint.length === 0 ? 0.6 : 1
                                }}
                            >
                                Generate Paperwork ({selectedRoutesToPrint.length})
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delivery Notes Modal */}
            {showDeliveryNotesModal && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.5)',
                    zIndex: 1060,
                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}>
                    <div style={{
                        backgroundColor: theme === 'light' ? '#fff' : '#2a2a2a',
                        color: theme === 'light' ? '#333' : '#eee',
                        borderRadius: 10,
                        padding: '25px 30px',
                        width: '90%',
                        minWidth: 600,
                        maxWidth: 800,
                        maxHeight: '80vh',
                        boxShadow: '0 2px 12px rgba(0,0,0,0.2)',
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden'
                    }}>
                        {/* Header */}
                        <div style={{
                            marginBottom: 20,
                            paddingBottom: 15,
                            borderBottom: `1px solid ${theme === 'light' ? '#eee' : '#444'}`,
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        }}>
                            <h3 style={{ margin: 0, fontSize: '1.5em' }}>
                                Delivery Notes — {selectedDate ? new Date(selectedDate).toLocaleDateString() : 'No Date'}
                            </h3>
                            <button
                                onClick={() => { setShowDeliveryNotesModal(false); setSelectedDeliveryNoteRoutes([]); }}
                                style={{ background: 'transparent', border: 'none', fontSize: '24px', cursor: 'pointer', color: theme === 'light' ? '#666' : '#ccc', padding: 0, width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                            >×</button>
                        </div>

                        {/* Body */}
                        <div style={{ flex: 1, overflow: 'auto' }}>
                            {completedRoutesForDay.length === 0 ? (
                                <p style={{ textAlign: 'center', color: theme === 'light' ? '#666' : '#aaa', fontSize: '1.1em', marginTop: 20 }}>
                                    No completed routes found for this date.
                                </p>
                            ) : (
                                <>
                                    <div style={{ marginBottom: 16 }}>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 'bold', cursor: 'pointer', fontSize: '1.1em' }}>
                                            <input
                                                type="checkbox"
                                                checked={selectedDeliveryNoteRoutes.length === completedRoutesForDay.length && completedRoutesForDay.length > 0}
                                                onChange={(e) => handleSelectAllDeliveryNotes(e.target.checked)}
                                                style={{ marginRight: 5 }}
                                            />
                                            Select All ({completedRoutesForDay.length})
                                        </label>
                                    </div>
                                    {completedRoutesForDay.map((route) => (
                                        <div
                                            key={route.route_uuid}
                                            style={{
                                                padding: 15,
                                                margin: '10px 0',
                                                border: `1px solid ${theme === 'light' ? '#ddd' : '#555'}`,
                                                borderRadius: 8,
                                                backgroundColor: selectedDeliveryNoteRoutes.includes(route.route_uuid)
                                                    ? (theme === 'light' ? '#f0f8ff' : '#2c3e50')
                                                    : 'transparent',
                                                transition: 'all 0.2s ease'
                                            }}
                                        >
                                            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 12, cursor: 'pointer', width: '100%' }}>
                                                <input
                                                    type="checkbox"
                                                    checked={selectedDeliveryNoteRoutes.includes(route.route_uuid)}
                                                    onChange={(e) => handleDeliveryNoteSelection(route.route_uuid, e.target.checked)}
                                                    style={{ marginTop: 2 }}
                                                />
                                                <div style={{ flex: 1 }}>
                                                    <div style={{ fontWeight: 'bold', marginBottom: 6, fontSize: '1.1em' }}>
                                                        {route.job} — {route.route || 'No route'}
                                                    </div>
                                                    <div style={{ fontSize: '0.95em', opacity: 0.8, marginBottom: 4 }}>
                                                        Driver: {route.driver || 'Not assigned'}
                                                    </div>
                                                    <div style={{ fontSize: '0.85em', opacity: 0.7 }}>
                                                        {route.postcode} | {route.route_ref || 'No consignment'}
                                                    </div>
                                                </div>
                                            </label>
                                        </div>
                                    ))}
                                </>
                            )}
                        </div>

                        {/* Footer */}
                        <div style={{
                            borderTop: `1px solid ${theme === 'light' ? '#eee' : '#444'}`,
                            paddingTop: 15,
                            marginTop: 15,
                            display: 'flex',
                            gap: 10,
                            justifyContent: 'flex-end'
                        }}>
                            <button
                                onClick={() => { setShowDeliveryNotesModal(false); setSelectedDeliveryNoteRoutes([]); }}
                                disabled={isGeneratingDeliveryNotes}
                                style={{ padding: '10px 20px', border: `1px solid ${theme === 'light' ? '#ccc' : '#555'}`, borderRadius: 6, background: 'transparent', color: theme === 'light' ? '#666' : '#ccc', cursor: 'pointer', fontSize: '1em' }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleGenerateDeliveryNotes}
                                disabled={selectedDeliveryNoteRoutes.length === 0 || isGeneratingDeliveryNotes}
                                style={{
                                    padding: '10px 20px',
                                    border: 'none',
                                    borderRadius: 6,
                                    background: (selectedDeliveryNoteRoutes.length === 0 || isGeneratingDeliveryNotes) ? '#ccc' : '#28a745',
                                    color: '#fff',
                                    cursor: (selectedDeliveryNoteRoutes.length === 0 || isGeneratingDeliveryNotes) ? 'not-allowed' : 'pointer',
                                    fontSize: '1em',
                                    opacity: (selectedDeliveryNoteRoutes.length === 0 || isGeneratingDeliveryNotes) ? 0.6 : 1
                                }}
                            >
                                {isGeneratingDeliveryNotes
                                    ? `Generating... (${selectedDeliveryNoteRoutes.length})`
                                    : `Download Delivery Notes (${selectedDeliveryNoteRoutes.length})`}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* show add trailer reg for external trailers */}
            {openAddTrailerReg && (
                <SingleValueInput
                    theme={theme}
                    header="External Trailer Number"
                    inputTag="Trailer:"
                    isOpen={openAddTrailerReg}
                    setIsOpen={setOpenAddTrailerReg}
                    returnData={(reg) => updateRouteData(selectedRouteUuid, {
                        trailer_id: 1,
                        trailer_reg: reg.toUpperCase(),
                        trailer_type: 'TRAILER',
                        external_trailer: reg.toUpperCase()
                    })}
                />
            )}

            {/* show costings input */}
            {openCostings && (
                <SingleValueInput
                    theme={theme}
                    header="Add Costings"
                    inputTag="Costings £"
                    isOpen={openCostings}
                    setIsOpen={setOpenCostings}
                    returnData={(costings) => addNewCostings(costings, selectedRouteUuid)}
                />
            )}

            {/* show invoice company input
            {openInvoiceTo && (
                <SingleValueInput
                    theme={theme}
                    header="Invoice Company"
                    inputTag="Invoice to"
                    isOpen={openInvoiceTo}
                    setIsOpen={setOpenInvoiceTo}
                    returnData={(company) => addNewInvoiceTo(company, selectedRouteUuid)}
                />
            )} */}
    
            {/* Default Calendar View */}
            {!isLoading && (
                <>
                    <div className="header">
                        <button onClick={() => changeMonth(subMonths(currentDate, 1))}>&lt;</button>
                        <h2>{format(currentDate, "MMMM yyyy")}</h2>
                        <button onClick={() => changeMonth(addMonths(currentDate, 1))}>&gt;</button>
                    </div>
                    <div className="day-names">
                        <span>Sun</span>
                        <span>Mon</span>
                        <span>Tue</span>
                        <span>Wed</span>
                        <span>Thu</span>
                        <span>Fri</span>
                        <span>Sat</span>
                    </div>
                    <AnimatePresence mode="wait" style={{ zIndex: 0, position: 'static' }}>
                        <motion.div
                            key={currentDate}
                            initial={{ opacity: 0, x: 100 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -100 }}
                            transition={{ duration: 0.5 }}
                            className="days-grid"
                            style={{ zIndex: 0, position: 'static' }}
                        >
                            {generateCalendar()}
                        </motion.div>
                    </AnimatePresence>
                    <AnimatePresence>{selectedDate && renderDayView()}</AnimatePresence>
                </>
            )}
            {/* Route completion details modal */}
            {showCompletionModal && selectedCompletionData && (
                <div 
                    className="modal-overlay" 
                    style={{
                        position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
                        background: 'rgba(0,0,0,0.75)', zIndex: 9999, display: 'flex', justifyContent: 'center', alignItems: 'center'
                    }}
                    onClick={(e) => {
                        e.stopPropagation();
                        // Only close if clicking on the backdrop itself
                        if (e.target === e.currentTarget) {
                            setShowCompletionModal(false);
                            setSelectedCompletionData(null);
                            setSelectedCompletionRoute(null);
                        }
                    }}
                >
                    <div 
                        style={{
                            background: theme === 'light' ? '#fff' : '#222',
                            color: theme === 'light' ? '#222' : '#fff',
                            borderRadius: 10,
                            border: '2px solid rgba(34, 197, 94, 0.8)',
                            padding: 28,
                            minWidth: 520,
                            maxWidth: 780,
                            maxHeight: '85vh',
                            boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
                            position: 'relative',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 0,
                            overflowY: 'auto',
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Header */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18, paddingBottom: 14, borderBottom: `1px solid ${theme === 'light' ? '#ddd' : '#444'}` }}>
                            <h3 style={{ margin: 0, fontSize: '1.3em', color: 'rgba(34, 197, 94, 0.9)' }}>
                                &#10003; Job Completion — {selectedCompletionData.route_ref || selectedCompletionData.route_uuid}
                            </h3>
                            <button onClick={() => { setShowCompletionModal(false); setSelectedCompletionData(null); setSelectedCompletionRoute(null); }} style={{ background: 'transparent', border: 'none', fontSize: 22, cursor: 'pointer', color: theme === 'light' ? '#444' : '#ccc', lineHeight: 1 }}>&times;</button>
                        </div>

                        {/* Route info */}
                        {selectedCompletionRoute && (
                            <div style={{ display: 'flex', gap: 24, marginBottom: 14, flexWrap: 'wrap', padding: '10px 14px', borderRadius: 6, background: theme === 'light' ? '#f5f5f5' : '#2a2a2a', border: `1px solid ${theme === 'light' ? '#e0e0e0' : '#3a3a3a'}` }}>
                                {selectedCompletionRoute.driver && (
                                    <div><span style={{ opacity: 0.6, fontSize: '0.8em', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Driver</span><br /><strong>{selectedCompletionRoute.driver}</strong></div>
                                )}
                                {selectedCompletionRoute.truck_reg && (
                                    <div><span style={{ opacity: 0.6, fontSize: '0.8em', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Truck</span><br /><strong>{selectedCompletionRoute.truck_reg}</strong></div>
                                )}
                                {selectedCompletionRoute.trailer_reg && (
                                    <div><span style={{ opacity: 0.6, fontSize: '0.8em', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Trailer</span><br /><strong>{selectedCompletionRoute.trailer_reg}</strong></div>
                                )}
                                {selectedCompletionRoute.job_comments && (
                                    <div style={{ flexBasis: '100%' }}><span style={{ opacity: 0.6, fontSize: '0.8em', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Job Comments</span><br /><span>{selectedCompletionRoute.job_comments}</span></div>
                                )}
                            </div>
                        )}

                        {/* Signed by / date */}
                        <div style={{ display: 'flex', gap: 24, marginBottom: 14, flexWrap: 'wrap' }}>
                            {selectedCompletionData.signed_by && (
                                <div><span style={{ opacity: 0.6, fontSize: '0.85em' }}>Signed by</span><br /><strong>{selectedCompletionData.signed_by}</strong></div>
                            )}
                            {selectedCompletionData.signed_at && (
                                <div><span style={{ opacity: 0.6, fontSize: '0.85em' }}>Signed at</span><br /><strong>{new Date(selectedCompletionData.signed_at).toLocaleString()}</strong></div>
                            )}
                            {selectedCompletionData.submitted_at && (
                                <div><span style={{ opacity: 0.6, fontSize: '0.85em' }}>Submitted</span><br /><strong>{new Date(selectedCompletionData.submitted_at).toLocaleString()}</strong></div>
                            )}
                        </div>

                        {/* GPS */}
                        {(selectedCompletionData.latitude || selectedCompletionData.geocoded_address) && (
                            <div style={{ marginBottom: 14 }}>
                                <span style={{ opacity: 0.6, fontSize: '0.85em' }}>Location</span><br />
                                {selectedCompletionData.geocoded_address && <div>{selectedCompletionData.geocoded_address}</div>}
                                {selectedCompletionData.latitude && selectedCompletionData.longitude && (
                                    <div 
                                        style={{ 
                                            fontSize: '0.85em', 
                                            opacity: 0.9,
                                            cursor: 'pointer',
                                            color: '#007bff',
                                            textDecoration: 'underline',
                                            padding: '4px 8px',
                                            border: '1px solid #007bff20',
                                            borderRadius: '4px',
                                            display: 'inline-block',
                                            marginTop: '4px'
                                        }}
                                        onClick={() => handleLocationClick(
                                            selectedCompletionData.latitude,
                                            selectedCompletionData.longitude,
                                            selectedCompletionData.geocoded_address
                                        )}
                                        title="Click to view on map"
                                    >
                                        📍 {selectedCompletionData.latitude}, {selectedCompletionData.longitude}{selectedCompletionData.location_accuracy ? ` (±${Math.round(selectedCompletionData.location_accuracy)}m)` : ''}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Notes */}
                        {selectedCompletionData.completion_notes && (
                            <div style={{ marginBottom: 14 }}>
                                <span style={{ opacity: 0.6, fontSize: '0.85em' }}>Notes</span><br />
                                <div style={{ whiteSpace: 'pre-wrap' }}>{selectedCompletionData.completion_notes}</div>
                            </div>
                        )}

                        {selectedCompletionData.driver_comments && (
                            <div style={{ marginBottom: 14 }}>
                                <span style={{ opacity: 0.6, fontSize: '0.85em' }}>Driver Comments</span><br />
                                <div style={{ whiteSpace: 'pre-wrap' }}>{selectedCompletionData.driver_comments}</div>
                            </div>
                        )}

                        {/* Customer Signature */}
                        {selectedCompletionData.customer_signature_data && (() => {
                            try {
                                // Check if signature data is already a data URL or needs parsing
                                let signatureUrl = selectedCompletionData.customer_signature_data;
                                
                                // If it's a JSON string, parse it to get the signature URL
                                if (typeof signatureUrl === 'string' && signatureUrl.startsWith('{')) {
                                    const sigData = JSON.parse(signatureUrl);
                                    signatureUrl = sigData?.signature;
                                } else if (typeof signatureUrl !== 'string') {
                                    // If it's already an object, get the signature property
                                    signatureUrl = signatureUrl?.signature;
                                }
                                
                                return signatureUrl ? (
                                    <div style={{ marginBottom: 14 }}>
                                        <span style={{ opacity: 0.6, fontSize: '0.85em' }}>Customer Signature</span><br />
                                        {selectedCompletionData.signed_by && (
                                            <div style={{ marginBottom: 8, fontSize: '0.9em', fontWeight: 'bold', color: theme === 'light' ? '#333' : '#fff' }}>
                                                {selectedCompletionData.signed_by}
                                            </div>
                                        )}
                                        <img 
                                            src={signatureUrl} 
                                            alt="Customer signature" 
                                            style={{ 
                                                maxWidth: '100%', 
                                                maxHeight: 160, 
                                                border: `1px solid ${theme === 'light' ? '#ccc' : '#555'}`, 
                                                borderRadius: 6, 
                                                marginTop: 6, 
                                                background: '#fff',
                                                cursor: 'pointer'
                                            }}
                                            onClick={() => {
                                                handleImageClick(signatureUrl, `Customer Signature - ${selectedCompletionData.signed_by || 'Unknown'}`);
                                            }}
                                            onError={(e) => {
                                                console.error('Signature image failed to load:', signatureUrl);
                                                e.target.style.display = 'none';
                                            }}
                                            title="Click to view larger"
                                        />
                                    </div>
                                ) : (
                                    <div style={{ marginBottom: 14, padding: 10, background: 'rgba(255,255,0,0.1)', borderRadius: 4 }}>
                                        <span style={{ opacity: 0.6, fontSize: '0.85em' }}>Customer Signature</span><br />
                                        <em>No signature image available</em>
                                    </div>
                                );
                            } catch (error) { 
                                console.error('Error processing signature data:', error);
                                return (
                                    <div style={{ marginBottom: 14, padding: 10, background: 'rgba(255,0,0,0.1)', borderRadius: 4 }}>
                                        <span style={{ opacity: 0.6, fontSize: '0.85em' }}>Customer Signature</span><br />
                                        <em>Error loading signature</em>
                                    </div>
                                );
                            }
                        })()}

                        {/* Delivery images */}
                        {selectedCompletionData.delivery_images?.length > 0 && (
                            <div style={{ marginBottom: 14 }}>
                                <span style={{ opacity: 0.6, fontSize: '0.85em' }}>Delivery Photos ({selectedCompletionData.delivery_images.length})</span>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 6 }}>
                                    {selectedCompletionData.delivery_images.map((url, i) => (
                                        <img 
                                            key={i} 
                                            src={url} 
                                            alt={`Delivery ${i + 1}`} 
                                            style={{ 
                                                width: 100, 
                                                height: 100, 
                                                objectFit: 'cover', 
                                                borderRadius: 6, 
                                                border: `1px solid ${theme === 'light' ? '#ccc' : '#555'}`,
                                                cursor: 'pointer'
                                            }}
                                            onClick={() => handleImageClick(url, `Delivery Photo ${i + 1}`)}
                                            onError={(e) => { e.target.style.opacity = '0.3'; }}
                                            title="Click to view larger"
                                        />
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Paperwork images */}
                        {selectedCompletionData.paperwork_images?.length > 0 && (
                            <div style={{ marginBottom: 14 }}>
                                <span style={{ opacity: 0.6, fontSize: '0.85em' }}>Paperwork Photos ({selectedCompletionData.paperwork_images.length})</span>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 6 }}>
                                    {selectedCompletionData.paperwork_images.map((url, i) => (
                                        <img 
                                            key={i} 
                                            src={url} 
                                            alt={`Paperwork ${i + 1}`} 
                                            style={{ 
                                                width: 100, 
                                                height: 100, 
                                                objectFit: 'cover', 
                                                borderRadius: 6, 
                                                border: `1px solid ${theme === 'light' ? '#ccc' : '#555'}`,
                                                cursor: 'pointer'
                                            }}
                                            onClick={() => handleImageClick(url, `Paperwork Photo ${i + 1}`)}
                                            onError={(e) => { e.target.style.opacity = '0.3'; }}
                                            title="Click to view larger"
                                        />
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Footer close button */}
                        <div style={{ marginTop: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: `1px solid ${theme === 'light' ? '#eee' : '#444'}`, paddingTop: 14, gap: 8, flexWrap: 'wrap' }}>
                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                {selectedCompletionData?.delivery_images?.length > 0 && (
                                    <button
                                        onClick={() => handleDownloadAll(selectedCompletionData.delivery_images, 'Delivery_Photo')}
                                        style={{ padding: '8px 16px', border: `1px solid ${theme === 'light' ? '#ccc' : '#555'}`, borderRadius: 6, background: 'transparent', color: theme === 'light' ? '#555' : '#ccc', cursor: 'pointer', fontSize: '0.9em' }}
                                    >
                                        ⬇ Download Photos ({selectedCompletionData.delivery_images.length})
                                    </button>
                                )}
                                {selectedCompletionData?.paperwork_images?.length > 0 && (
                                    <button
                                        onClick={() => handleDownloadAll(selectedCompletionData.paperwork_images, 'Paperwork_Photo')}
                                        style={{ padding: '8px 16px', border: `1px solid ${theme === 'light' ? '#ccc' : '#555'}`, borderRadius: 6, background: 'transparent', color: theme === 'light' ? '#555' : '#ccc', cursor: 'pointer', fontSize: '0.9em' }}
                                    >
                                        ⬇ Download Paperwork ({selectedCompletionData.paperwork_images.length})
                                    </button>
                                )}
                            </div>
                            <button
                                onClick={() => { setShowCompletionModal(false); setSelectedCompletionData(null); setSelectedCompletionRoute(null); }}
                                style={{ padding: '8px 22px', border: `1px solid ${theme === 'light' ? '#ccc' : '#555'}`, borderRadius: 6, background: 'transparent', color: theme === 'light' ? '#555' : '#ccc', cursor: 'pointer', fontSize: '1em' }}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Image Viewer Modal */}
            {showImageModal && (
                <div
                    className="modal show"
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(0,0,0,0.8)', position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', zIndex: 9999 }}
                    tabIndex="-1"
                    onClick={(e) => { if (e.target === e.currentTarget) handleCloseImageModal(); }}
                >
                    <div className="modal-dialog modal-xl modal-dialog-centered" style={{ margin: 'auto', maxHeight: '90vh', width: '100%' }}>
                        <div className={`modal-content ${theme === 'light' ? 'bg-light text-dark' : 'bg-dark text-light'}`}>
                            <div className={`modal-header ${theme === 'light' ? 'bg-light text-dark border-secondary' : 'bg-dark text-light border-secondary'}`}>
                                <h5 className="modal-title">📷 {selectedImageTitle}</h5>
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-secondary ms-auto"
                                    onClick={handleCloseImageModal}
                                    style={{ fontSize: '18px', padding: '2px 8px' }}
                                >
                                    ×
                                </button>
                            </div>
                            <div
                                className={`modal-body text-center ${theme === 'light' ? 'bg-light' : 'bg-dark'}`}
                                style={{ padding: '20px', maxHeight: 'calc(90vh - 130px)', overflow: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '200px' }}
                            >
                                {isImageLoading ? (
                                    <div className="spinner-border" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                ) : selectedImageUrl ? (
                                    <img
                                        src={selectedImageUrl}
                                        alt={selectedImageTitle}
                                        style={{ maxWidth: '100%', maxHeight: 'calc(90vh - 190px)', objectFit: 'contain', border: theme === 'light' ? '1px solid #ddd' : '1px solid #555' }}
                                        onError={(e) => {
                                            e.target.style.display = 'none';
                                            showAlert({ message: 'Failed to load image.', type: 'danger', buttons: [{ type: 'danger', label: 'Close' }] });
                                            handleCloseImageModal();
                                        }}
                                    />
                                ) : null}
                            </div>
                            <div className={`modal-footer ${theme === 'light' ? 'bg-light border-secondary' : 'bg-dark border-secondary'}`}>
                                <button type="button" className="btn btn-secondary" onClick={handleCloseImageModal}>
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            
            {/* Location Map Modal */}
            <LocationMapModal
                show={showLocationModal}
                onHide={handleCloseLocationModal}
                location={selectedLocation}
                title="Route Completion Location"
                zIndex={99998}
            />

            {/* Pod Off Modal */}
            <PodOffModal
                show={showPodOffModal}
                onClose={() => { setShowPodOffModal(false); setPodOffRoute(null); }}
                route={podOffRoute}
                theme={theme}
                onSuccess={handlePodOffSuccess}
            />

            <Modal
                show={showInvoiceCodeModal}
                onHide={() => setShowInvoiceCodeModal(false)}
                centered
                className={`route-invoice-code-modal ${theme}`}
                backdropClassName="route-invoice-code-backdrop"
            >
                <Modal.Header closeButton closeVariant={theme === 'dark' ? 'white' : undefined}>
                    <Modal.Title>Generate invoice drafts</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>Generate drafts for all eligible routes on {selectedDate}.</p>
                    {invoiceRouteTypes.normal && (
                        <Form.Group className="mb-3">
                            <Form.Label>Normal routes account code</Form.Label>
                            <Form.Select
                                value={normalInvoiceAccountCodeId}
                                onChange={event => setNormalInvoiceAccountCodeId(event.target.value)}
                            >
                                <option value="">Select normal revenue code</option>
                                {invoiceAccountCodes.filter(accountCode => !accountCode.is_pallet_network).map(accountCode => (
                                    <option key={accountCode.id} value={accountCode.id}>
                                        {accountCode.code} — {accountCode.name}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                    )}
                    {invoiceRouteTypes.network && (
                        <Form.Group>
                            <Form.Label>Network Freight account code</Form.Label>
                            <Form.Select
                                value={networkInvoiceAccountCodeId}
                                onChange={event => setNetworkInvoiceAccountCodeId(event.target.value)}
                            >
                                <option value="">Select pallet-network revenue code</option>
                                {invoiceAccountCodes.filter(accountCode => accountCode.is_pallet_network).map(accountCode => (
                                    <option key={accountCode.id} value={accountCode.id}>
                                        {accountCode.code} — {accountCode.name}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowInvoiceCodeModal(false)}>Cancel</Button>
                    <Button
                        onClick={generateSelectedInvoiceDrafts}
                        disabled={
                            (invoiceRouteTypes.normal && !normalInvoiceAccountCodeId)
                            || (invoiceRouteTypes.network && !networkInvoiceAccountCodeId)
                        }
                    >
                        Generate drafts
                    </Button>
                </Modal.Footer>
            </Modal>

        </div>
    );
    
};

export default RouteCalendar;
